/**
 * PluginBridge — Host-side bridge for communicating with plugin iframes.
 *
 * Handles postMessage communication, permission enforcement,
 * rate limiting, and proxying requests to the FIAS backend.
 */

import { ACCEPT_ENTRY_RE, MAX_ACCEPT_ENTRIES } from '@/components/files/file-dialog/accept';
import { invokeClientEntity, getClientEntityRegistration } from '@/lib/client-entity-registry';
import { sessionCredentials } from '@/lib/embed-document';
import {
  entityInvokeRequiredPermission,
  INTEGRATION_ENTITY_PERMISSIONS,
  PLUGIN_STORAGE_ENTITY_ID,
  PLUGIN_DATASTORE_ENTITY_ID,
  PLUGIN_WORKSPACES_ENTITY_ID,
  PLUGIN_STORE_ENTITY_ID,
  VAULT_USER_DOCUMENTS_ENTITY_ID,
  VAULT_ARCHE_DOCUMENTS_ENTITY_ID,
} from '@/lib/integration-entity-permissions';
import { PluginSubscriptionManager } from './plugin-subscription-manager';
import { getWebSocket } from '@/lib/websocket';
import { WEBSOCKET_ENDPOINT } from '@/lib/api';
import { authApi } from '@/lib/auth-api';
import { attachCsrfHeader } from '@/lib/csrf';
// Plugin permission scopes. Imported type-only (erased at build) from the
// SDK's generated permissions — the client-safe mirror of @fias/db-types,
// generated by `pnpm generate:sdk-types` and pinned in lockstep by
// tests/architecture/sdk-permissions-match-db-types.test.ts. Importing rather
// than re-declaring the union here avoids drift: a prior hand-maintained copy
// silently fell behind db-types. A type-only import adds no runtime dependency
// on the server-only db-types package (the bridge ships in plugin iframes).
import type { PluginPermission, FiasVendoredAssets, FiasTextModel } from '@fias/arche-sdk';
import type {
  PluginDeclaredAiAction,
  PluginRuntimeAiDeclaration,
} from '@/components/fias-ai/types';
// Client-local handoff mirror. A RUNTIME import here is consistent with this
// module's no-workspace-package rule: the mirror exists precisely so client
// code can validate handoff payloads without reaching for server-only
// db-types, and validating at the bridge is what lets a plugin see WHY its
// payload was refused instead of having it silently dropped on arrival.
import {
  handoffTargetsArche,
  isHandoffReplyKind,
  parseArcheHandoffPayload,
  type ArcheHandoffPayload,
} from '@/components/fias-ai/handoff';
import { invalidateVaultDocumentsAfterHostWrite } from './vaultHostWrites';

/**
 * Arche id formats accepted by the `open_arche` op. The regex mirrors
 * `isValidArcheId` in `@fias/db-types` (`arche.ts`) — inlined rather than
 * imported because this bridge takes no runtime dependency on that
 * server-only package.
 */
function isValidArcheIdFormat(id: string): boolean {
  return /^arc_[a-f0-9]{32}$/.test(id) || /^arche_[a-z0-9_]+$/.test(id);
}

/**
 * `arc_<32hex>` ids are GENERATED — every plugin arche and every arche the
 * builder mints. `arche_<slug>` ids are seeded, first-party arches, and those
 * are the only ones with hand-registered routes UNDER their own prefix in
 * `App.tsx` (`/a/arche_document_sign/sign/:token`, `/a/arche_calendar/book/:slug`,
 * `/a/arche_fias_transfer/d/:token` — several of which need no auth).
 *
 * A generated arche, by contrast, owns its entire `/a/<id>/*` space through the
 * catch-all, so a sub-path there reaches nothing but that arche's own router.
 * That is why `open_arche` accepts a sub-path for these ids and only these:
 * without the split, a plugin could steer the top frame into a first-party
 * arche's token-gated flow, which is a capability it has never had.
 * Pinned by `client/src/test/architecture/arche-structure.test.ts`.
 */
function acceptsSubPath(archeId: string): boolean {
  return /^arc_[a-f0-9]{32}$/.test(archeId);
}

/**
 * Sub-path a plugin may request under `/a/<archeId>/`.
 *
 * Mirrors `SITE_PAGE_PATH_RE` in `@fias/db-types` (`plugin.ts`) minus its
 * leading slash — the same one-or-two-segment `[A-Za-z0-9_-]` alphabet that the
 * CloudFront viewer function is already willing to rewrite. Reusing that
 * grammar rather than inventing one means traversal (`..`), encoded traversal,
 * spaces, quotes, protocol-relative `//host` and query/fragment smuggling are
 * all rejected by construction, not by a blocklist. Inlined for the same reason
 * the id regex is: this bridge takes no runtime dependency on db-types.
 */
function isValidArcheSubPath(path: string): boolean {
  return /^[A-Za-z0-9_-]+(?:\/[A-Za-z0-9_-]+)?$/.test(path);
}

// Bridge message types (must match the SDK's types)
interface BridgeMessage {
  type: string;
  messageId: string;
  payload: unknown;
}

/**
 * Bridge protocol version negotiation — host side.
 *
 *   HOST_BRIDGE_PROTOCOL_VERSION
 *     The protocol version this host SPEAKS. Bumped on backward-
 *     incompatible additions to the message shape.
 *
 *   HOST_REQUIRES_SDK_PROTOCOL_VERSION
 *     Minimum SDK protocol version this host accepts. If a plugin
 *     reports a lower version in its `ready` message, the host fires
 *     `onIncompatibleSdk` so the UI can render a "plugin needs update"
 *     affordance instead of letting the plugin call ops the host has
 *     removed.
 *
 * SYNC NOTE: must rise in lockstep with
 * packages/arche-sdk/src/protocol.ts when the protocol changes.
 */
const HOST_BRIDGE_PROTOCOL_VERSION = 1;
const HOST_REQUIRES_SDK_PROTOCOL_VERSION = 1;

/**
 * Max time the host waits for a plugin to return its state snapshot after a
 * `preview_state_capture` request. Plugins that support the protocol reply
 * within a tick (the SDK handler is synchronous), so this only bounds the
 * pathological case; it never delays plugins that don't advertise support
 * (those skip capture entirely).
 */
const PREVIEW_STATE_CAPTURE_TIMEOUT_MS = 250;

/**
 * Cap on the serialized size of a captured preview-state snapshot. Mirrors the
 * defensive posture of the preview storage budget — a buggy plugin returning a
 * huge object shouldn't be ferried across the reload. Oversized snapshots are
 * dropped (logged), so the worst case is a non-restored report, not an OOM.
 */
const PREVIEW_STATE_MAX_BYTES = 512 * 1024;

// ── Fias AI action declaration (`ai_actions_declare` / `ai_state` lane) ──────
// This lane is fire-and-forget metadata registration (same family as resize/
// toast), which means checkRateLimit never sees it — every cap is enforced
// right here. Bounds mirror `MANIFEST_FIAS_AI_LIMITS` in @fias/db-types
// (the client cannot import db-types at runtime, so the numbers are
// duplicated deliberately; server-side truth is the assistant turn schema).
/** Max actions one runtime declaration may carry (= the turn schema's cap). */
const AI_ACTIONS_MAX_COUNT = 50;
/** Serialized cap on a whole declaration — action text enters the model's tool list. */
const AI_ACTIONS_MAX_DECLARATION_BYTES = 64 * 1024;
/**
 * Re-declaration window (unmetered lane). VALID declarations inside the
 * window are trailing-edge coalesced — held, latest wins, applied at the
 * window edge — never dropped: the content-diffed SDK won't re-send one.
 */
const AI_ACTIONS_MIN_REDECLARE_INTERVAL_MS = 1_000;

/** `ai_open` debounce — a plugin may request the assistant panel at most once
 * per window. The renderer layers a longer cooldown after a USER-close on top
 * (the bridge cannot see panel state), so this bound only has to stop bursts. */
const AI_OPEN_MIN_INTERVAL_MS = 10_000;
/**
 * ai_state coalescing window (trailing edge, latest wins). Each accepted
 * snapshot re-renders the host, so a plugin pushing per-frame must not
 * drive the host at frame rate.
 */
const AI_STATE_MIN_INTERVAL_MS = 250;
/**
 * Serialized cap on an `ai_state` snapshot. Deliberately small: the server
 * does NOT bound `archeContext.state` and JSON-stringifies it into the system
 * prompt on EVERY turn — an unbounded snapshot silently balloons the user's
 * token bill. Oversized snapshots are dropped (logged); the last good one
 * stays current.
 */
const AI_STATE_MAX_BYTES = 16 * 1024;
/**
 * How long a dispatched ai_action may run in the plugin before the bridge
 * rejects it. Below the assistant loop's own applyAction timeout (30s) so the
 * failure carries this precise message rather than a generic race timeout.
 */
const AI_ACTION_DISPATCH_TIMEOUT_MS = 25_000;
const AI_ACTION_ID_REGEX = /^[a-z][a-z0-9_]*$/;
/** = MANIFEST_FIAS_AI_LIMITS.MAX_PARAMETER_SCHEMA_BYTES — lanes must agree. */
const AI_ACTION_MAX_PARAMETER_SCHEMA_BYTES = 4096;
/** = MANIFEST_FIAS_AI_LIMITS.MAX_AVAILABLE_WHEN_KEYS — lanes must agree. */
const AI_ACTION_MAX_AVAILABLE_WHEN_KEYS = 8;
/**
 * C0 controls + DEL, Unicode bidi controls, and zero-width/format chars —
 * prompt-injection + text-spoofing vectors. Mirrors CONTROL_CHARS_REGEX in
 * @fias/db-types (lockstep-pinned by the fias-ai bounds arch test).
 */
const AI_CONTROL_CHARS_REGEX =
  // eslint-disable-next-line no-control-regex -- matching control chars IS this guard's job
  /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/;

type ParseResult<T> = { value: T } | { error: string };

/**
 * Deep-scan every string (keys and values) in a JSON-shaped payload for
 * banned characters. NOT a serialized-string scan: JSON.stringify ESCAPES
 * C0 controls (ESC becomes backslash-u text), so scanning the serialization
 * silently misses exactly the classic injection bytes.
 */
function containsBannedChars(value: unknown): boolean {
  if (typeof value === 'string') return AI_CONTROL_CHARS_REGEX.test(value);
  if (Array.isArray(value)) return value.some(containsBannedChars);
  if (value && typeof value === 'object') {
    return Object.entries(value).some(
      ([k, v]) => AI_CONTROL_CHARS_REGEX.test(k) || containsBannedChars(v),
    );
  }
  return false;
}

/**
 * Parse-and-build one runtime-declared action. Everything here reaches the
 * model's tool list verbatim, so this is the trust boundary between
 * iframe-supplied text and the assistant. Constructs the typed action from
 * the VALIDATED fields (never a cast of the raw payload) so a field the
 * validator doesn't know about cannot ride along.
 */
function parseRuntimeAiAction(entry: unknown): ParseResult<PluginDeclaredAiAction> {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
    return { error: 'action must be an object' };
  }
  const a = entry as Record<string, unknown>;
  if (typeof a.id !== 'string' || a.id.length > 100 || !AI_ACTION_ID_REGEX.test(a.id)) {
    return { error: `invalid action id ${JSON.stringify(a.id)}` };
  }
  if (
    typeof a.description !== 'string' ||
    a.description.trim().length === 0 ||
    a.description.length > 1000 ||
    AI_CONTROL_CHARS_REGEX.test(a.description)
  ) {
    return { error: `action "${a.id}": invalid description` };
  }
  if (
    !a.parameterSchema ||
    typeof a.parameterSchema !== 'object' ||
    Array.isArray(a.parameterSchema)
  ) {
    return { error: `action "${a.id}": parameterSchema must be an object` };
  }
  const serializedSchema = JSON.stringify(a.parameterSchema);
  // Same per-action bound the manifest lane enforces (MANIFEST_FIAS_AI_LIMITS
  // .MAX_PARAMETER_SCHEMA_BYTES) — the two lanes must not diverge on what a
  // schema may weigh, since both land verbatim in the model's tool list.
  if (serializedSchema.length > AI_ACTION_MAX_PARAMETER_SCHEMA_BYTES) {
    return {
      error: `action "${a.id}": parameterSchema must serialize to ≤ ${AI_ACTION_MAX_PARAMETER_SCHEMA_BYTES} bytes`,
    };
  }
  if (containsBannedChars(a.parameterSchema)) {
    return { error: `action "${a.id}": parameterSchema contains control characters` };
  }
  if (a.requiresConfirmation !== undefined && typeof a.requiresConfirmation !== 'boolean') {
    return { error: `action "${a.id}": requiresConfirmation must be a boolean` };
  }
  if (
    a.category !== undefined &&
    (typeof a.category !== 'string' ||
      a.category.length > 100 ||
      AI_CONTROL_CHARS_REGEX.test(a.category))
  ) {
    return { error: `action "${a.id}": invalid category` };
  }
  let availableWhen: Record<string, boolean | string> | undefined;
  if (a.availableWhen !== undefined) {
    if (!a.availableWhen || typeof a.availableWhen !== 'object' || Array.isArray(a.availableWhen)) {
      return { error: `action "${a.id}": availableWhen must be an object` };
    }
    const availableWhenEntries = Object.entries(a.availableWhen as Record<string, unknown>);
    // = MANIFEST_FIAS_AI_LIMITS.MAX_AVAILABLE_WHEN_KEYS — lanes must agree.
    if (availableWhenEntries.length > AI_ACTION_MAX_AVAILABLE_WHEN_KEYS) {
      return {
        error: `action "${a.id}": availableWhen must declare ≤ ${AI_ACTION_MAX_AVAILABLE_WHEN_KEYS} keys`,
      };
    }
    availableWhen = {};
    for (const [key, value] of availableWhenEntries) {
      if (key.length === 0 || key.length > 64) {
        return { error: `action "${a.id}": invalid availableWhen key` };
      }
      if (typeof value !== 'boolean' && (typeof value !== 'string' || value.length > 100)) {
        return {
          error: `action "${a.id}": availableWhen["${key}"] must be a boolean or short string`,
        };
      }
      availableWhen[key] = value;
    }
  }
  return {
    value: {
      id: a.id,
      description: a.description,
      parameterSchema: a.parameterSchema as Record<string, unknown>,
      ...(a.requiresConfirmation !== undefined
        ? { requiresConfirmation: a.requiresConfirmation as boolean }
        : {}),
      ...(a.category !== undefined ? { category: a.category as string } : {}),
      ...(availableWhen !== undefined ? { availableWhen } : {}),
    },
  };
}

/**
 * Parse-and-build a whole runtime declaration. Atomic: one invalid action
 * rejects the declaration (mirrors the manifest validator — a partial tool
 * list is worse than none).
 */
function parseRuntimeAiDeclaration(
  payload: unknown,
): { declaration: PluginRuntimeAiDeclaration } | { error: string } {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { error: 'payload must be an object or null' };
  }
  const decl = payload as Record<string, unknown>;
  if (
    decl.description !== undefined &&
    (typeof decl.description !== 'string' ||
      decl.description.length > 2000 ||
      AI_CONTROL_CHARS_REGEX.test(decl.description))
  ) {
    return { error: 'invalid description' };
  }
  if (!Array.isArray(decl.actions) || decl.actions.length === 0) {
    return { error: 'actions must be a non-empty array' };
  }
  if (decl.actions.length > AI_ACTIONS_MAX_COUNT) {
    return { error: `${decl.actions.length} actions exceeds the ${AI_ACTIONS_MAX_COUNT} cap` };
  }
  let serializedBytes = 0;
  try {
    serializedBytes = JSON.stringify(decl).length;
  } catch {
    return { error: 'payload not serializable' };
  }
  if (serializedBytes > AI_ACTIONS_MAX_DECLARATION_BYTES) {
    return {
      error: `${serializedBytes} bytes exceeds the ${AI_ACTIONS_MAX_DECLARATION_BYTES}-byte cap`,
    };
  }
  const seen = new Set<string>();
  const actions: PluginDeclaredAiAction[] = [];
  for (const entry of decl.actions) {
    const parsed = parseRuntimeAiAction(entry);
    if ('error' in parsed) return parsed;
    if (seen.has(parsed.value.id)) {
      return { error: `duplicate action id "${parsed.value.id}"` };
    }
    seen.add(parsed.value.id);
    actions.push(parsed.value);
  }
  return {
    declaration: {
      ...(typeof decl.description === 'string' ? { description: decl.description } : {}),
      actions,
    },
  };
}

interface PluginBridgeOptions {
  archId: string;
  permissions: PluginPermission[];
  iframe: HTMLIFrameElement;
  onResize?: (height: number) => void;
  onToast?: (message: string, variant: string) => void;
  /**
   * Runtime Fias AI action declaration from the plugin (`ai_actions_declare`).
   * Fires only after the bridge's permission + bounds checks pass; `null`
   * clears a previous declaration. The HOST decides whether to honour it —
   * the runtime lane is gated on `arches.is_trusted`, which the renderer
   * holds and the bridge does not.
   */
  onAiActionsDeclare?: (declaration: PluginRuntimeAiDeclaration | null) => void;
  /** Plugin state snapshot for the assistant (`ai_state`), already size-capped. */
  onAiState?: (state: Record<string, unknown>) => void;
  /**
   * Plugin asked the host to open the global Fias AI panel (`ai_open`,
   * discoverability Part 2). Fires only after the permission gate and the
   * bridge-level debounce; the renderer applies its own after-user-close
   * cooldown before actually opening, so a plugin can never nag the panel
   * back open against the user's wishes.
   */
  onAiOpenRequest?: () => void;
  /**
   * `replace` rewrites the current history entry instead of pushing one — a
   * plugin correcting its own URL (a normalized link, a refused route) must not
   * leave an entry behind for Back to land on.
   */
  onNavigate?: (path: string, opts: { replace: boolean }) => void;
  /**
   * The `/a/<segment>` URL segment this plugin is CURRENTLY mounted under, as
   * it appears in the address bar. Supplied by PluginRenderer from the route
   * param; omitted by the builder/dev previews, which render a plugin on a
   * page belonging to a *different* arche and therefore have no segment of
   * this plugin's own to speak of.
   *
   * Read through a getter rather than captured as a value because the bridge
   * is built once per iframe load and outlives individual renders.
   */
  getUrlSegment?: () => string | undefined;
  /**
   * Cross-arche navigation requested via the `open_arche` op. `archeId` and the
   * optional `path` are already validated by the bridge; the host resolves them
   * to `/a/<archeId>[/<path>]` and navigates (same-tab) or, when `newTab` is
   * set, best-effort opens a new tab.
   */
  onOpenArche?: (
    archeId: string,
    opts: {
      newTab: boolean;
      path?: string;
      /** Validated handoff payload to hand the target arche, if any. */
      payload?: ArcheHandoffPayload;
      /**
       * The arche that asked. Stamped HERE from this bridge's own id — never
       * read from the plugin's message — so one plugin cannot claim to be
       * another and steer a handoff (or its eventual reply) somewhere it has
       * no business being.
       */
      originArcheId?: string;
    },
  ) => void;
  getTheme: () => unknown;
  /**
   * The workspace the user is acting in — the host's holder switcher — as a
   * Vault holder id, or null for Personal. Attached by the host to every
   * proxied `entity_invoke` as `actingHolderId`, OVERWRITING anything the
   * iframe put there: the plugin never chooses the workspace, the user does.
   * Read live (a getter, not a value) so a switch mid-session takes effect on
   * the next call. Server-side it is a scope, not a claim — the Vault
   * wrapper's trust check authorizes the pair — and only handlers that honour
   * it (the consented user-documents family) read it.
   */
  getActingHolderId?: () => string | null;
  /** Self-hosted vendored-library asset URLs (keyed by import specifier) for the
   *  init payload. Called only when the plugin declares
   *  `sandbox:vendored-libraries`; returns null when the CDN is unconfigured. */
  getVendoredAssets?: () => FiasVendoredAssets | null;
  /**
   * The live text-model catalog for the init payload (see
   * `plugin-text-models.ts`), or null while the host is still loading it — it
   * is then pushed with `sendTextModelsUpdate()`. Consulted only for plugins
   * holding `entities:invoke`.
   */
  getTextModels?: () => FiasTextModel[] | null;
  getUser: () => Promise<{ userId: string; displayName: string; avatar: string | null }>;
  bridgeApiUrl: string;
  getAuthToken: () => Promise<string>;
  /**
   * Cache-bust hook for expired capability tokens: invoked once when a
   * bridge call comes back BRIDGE_TOKEN_EXPIRED, immediately before the
   * single retry re-invokes getAuthToken(). Without it the retry would read
   * the same stale token back out of the caller's cache.
   */
  onAuthTokenExpired?: () => void;
  /**
   * When true, the bridge is hosting the Arche Builder preview. AI/image ops
   * route through the session-scoped hooks below; store/IAP stays simulated
   * and vault/surface/assets stay unsupported (see the per-case branches);
   * everything else (storage/data/workspaces) proxies to the LIVE plugins
   * bridge under the `preview_<sessionId>` sandbox tenant — production
   * behavior, production errors (ADR-012 Phase M3).
   */
  previewMode?: boolean;
  /**
   * When true, the bridge is hosting a Fias Forge TEST build (ADR-023,
   * `/a/<archeId>?channel=test`). Deliberately narrower than `previewMode`:
   * store/IAP runs the same simulated branch as the preview (no product is
   * ever charged from a test build — the server refuses store ops on a test
   * tenant with `TEST_CHANNEL_SIMULATED_CLIENT_SIDE`, so a bypass cannot
   * spend), and NOTHING else changes: every other op proxies to the live
   * bridge under the `test_<archeId>` tenant, where the server applies the
   * per-entity test-channel policy. The AI/image preview hooks above are
   * never consulted.
   */
  testChannel?: boolean;
  /** Custom entity_invoke handler for preview (avoids backend proxy for entity lookup). */
  onEntityInvoke?: (payload: {
    entityId: string | Record<string, unknown>;
    input: string;
    systemPrompt?: string;
    /** Vision inputs — forwarded to the preview ai-invoke route (parity with published entity_invoke). */
    images?: Array<{ mediaType: string; dataBase64: string }>;
    /** Prior conversation turns — forwarded like images (validated server-side). */
    history?: unknown;
    /** Per-call parameters; the preview route reads `reasoning`. */
    parameters?: Record<string, unknown>;
  }) => Promise<{ output: string }>;
  /** Custom image_generate handler for preview (avoids backend proxy for image config lookup). */
  onImageGenerate?: (payload: {
    entityId: string | Record<string, unknown>;
    prompt: string;
    size?: string;
    quality?: string;
    style?: string;
  }) => Promise<{
    imageUrl: string;
    imageRef: string;
    revisedPrompt?: string;
    provider: string;
    model: string;
  }>;
  /** Callback for bridge-level log events (e.g., burst detection warnings). */
  onLog?: (level: 'warn' | 'error', message: string) => void;
  /**
   * Fired when the SDK acknowledges init with a `sdkProtocolVersion`
   * lower than `HOST_REQUIRES_SDK_PROTOCOL_VERSION`. The host UI should
   * unmount the iframe and render a "plugin needs update" affordance.
   * Receives the actual SDK version the plugin reported.
   */
  onIncompatibleSdk?: (sdkProtocolVersion: number, minRequired: number) => void;
  /**
   * Fired when a retired op-family alias (storage_*, data_*, workspace_*, store_*)
   * is received from an old, immutable published bundle (ADR-012 Phase L Job 3
   * Step 2). The host UI should render a "plugin needs an update" affordance.
   * Receives the retired op name.
   */
  onRetiredCapability?: (op: string) => void;
  /**
   * Callback for IAP purchase requests. When set, store_purchase messages
   * invoke this instead of proxying directly to backend. The callback should
   * show a confirmation UI and resolve with the backend response on confirm,
   * or { success: false, error: 'USER_CANCELED' } on cancel.
   */
  onPurchaseRequest?: (product: {
    productIdentifier: string;
    name: string;
    description: string | null;
    priceCredits: number;
    type: string;
  }) => Promise<unknown>;
  /**
   * Callback for the Vault user-document picker (host-UI mediation, the
   * purchase-sheet pattern). When set, `vault_documents_pick` messages open
   * the host's picker + consent sheet; the plugin receives ONLY what the
   * user hand-picked and confirmed — metadata, never bytes (content flows
   * through the audited consented-read entity verbs afterwards). Resolve
   * with { canceled: true } on dismiss.
   */
  onVaultPickRequest?: (request: {
    maxDocuments: number;
    /** `write` = the plugin is asking for EDIT access; the sheet says so. */
    access: 'read' | 'write';
    /** Upgrade path: one document the app already holds a grant on. The HOST
     *  verifies that before skipping the picker for it. */
    documentId?: string;
    /** The types the arche can open — MIME types, `type/*` families,
     *  `.ext` extensions (validated here; at most 20). */
    accept?: string[];
  }) => Promise<
    | { canceled: true }
    | {
        documents: Array<{
          documentId: string;
          name: string;
          mimeType: string | null;
          sizeBytes: number | null;
          access: 'read' | 'write';
        }>;
      }
  >;
  /** Fired when the plugin answers `init` with `ready` — its SDK is listening.
   *  The host uses this to flush anything it could not deliver earlier. */
  onPluginReady?: () => void;
  /**
   * A plugin saving into the user's Documents (`upload({ destination:
   * 'documents' })`, `userVisible: true`, or `saveCopy`; host-UI mediation,
   * same class as `onVaultPickRequest`). The host shows its Save dialog — the
   * user chooses the folder, the name, and what the arche keeps — and performs
   * the write from the user's own session (the Save-to-Documents routes, which a
   * bridge token cannot reach). Resolve with what was saved, or
   * `{ canceled: true }` on dismiss.
   *
   * Absence fails CLOSED: the bridge rejects the save with `SAVE_DECLINED`
   * rather than quietly writing an arche-private document the user would
   * never find in their files.
   */
  onVaultSaveRequest?: (request: {
    /** The name the plugin proposes — UNTRUSTED; the dialog shows it scrubbed. */
    fileName: string;
    mimeType: string;
    bytes: Uint8Array<ArrayBuffer>;
  }) => Promise<
    | { saved: { documentId: string; name: string; access: 'none' | 'read' | 'write' } }
    | { canceled: true }
  >;
  /** Mock store products for preview mode. */
  previewStoreProducts?: Array<{
    productIdentifier: string;
    type: string;
    name: string;
    description: string | null;
    priceCredits: number;
    metadata: Record<string, unknown>;
    subscriptionPeriod?: string;
  }>;
  /**
   * Preview-mode only. A plugin state snapshot captured from the previous
   * iframe just before this (re)load — see `captureState()`. When the freshly
   * loaded plugin signals readiness and advertises preview-state support, the
   * bridge replays this snapshot so transient UI state (e.g. a generated
   * report) survives a rebuild. Ignored outside preview mode.
   */
  initialPreviewState?: unknown;
  /** Fired after `initialPreviewState` has been sent to the plugin for restore. */
  onPreviewStateRestored?: () => void;
}

/**
 * Rate limiting state per message type.
 */
interface RateLimitBucket {
  count: number;
  windowStart: number;
}

const RATE_LIMITS: Record<string, { maxPerMinute: number }> = {
  // Backstop against popup-bombing — a plugin that requests newTab opens
  // could otherwise spawn tabs in a loop. Tile clicks are well under this.
  open_arche: { maxPerMinute: 30 },
  entity_invoke: { maxPerMinute: 60 },
  client_entity_invoke: { maxPerMinute: 60 },
  image_generate: { maxPerMinute: 10 },
  audio_generate: { maxPerMinute: 10 },
  storage_write: { maxPerMinute: 120 },
  storage_read: { maxPerMinute: 300 },
  storage_list: { maxPerMinute: 60 },
  storage_delete: { maxPerMinute: 60 },
  data_put: { maxPerMinute: 120 },
  data_get: { maxPerMinute: 300 },
  data_query: { maxPerMinute: 60 },
  data_delete: { maxPerMinute: 60 },
  data_batch: { maxPerMinute: 30 },
  data_create_collection: { maxPerMinute: 10 },
  data_delete_collection: { maxPerMinute: 10 },
  data_list_collections: { maxPerMinute: 60 },
  // Workspace tenancy (ADR-010) — mirrors server-side DATA_STORE_RATE_LIMITS.
  workspace_create: { maxPerMinute: 20 },
  workspace_list: { maxPerMinute: 120 },
  workspace_get: { maxPerMinute: 120 },
  workspace_archive: { maxPerMinute: 20 },
  workspace_list_members: { maxPerMinute: 120 },
  workspace_add_member: { maxPerMinute: 30 },
  workspace_update_member: { maxPerMinute: 30 },
  workspace_remove_member: { maxPerMinute: 30 },
  store_get_products: { maxPerMinute: 60 },
  store_purchase: { maxPerMinute: 10 },
  store_get_entitlements: { maxPerMinute: 60 },
  store_get_purchase_history: { maxPerMinute: 30 },
  store_restore: { maxPerMinute: 10 },
  store_cancel_subscription: { maxPerMinute: 10 },
  // Host picker: user-gesture bound anyway; the cap only stops a broken
  // plugin from re-opening the sheet in a loop.
  vault_documents_pick: { maxPerMinute: 5 },
  vault_documents_list: { maxPerMinute: 120 },
  vault_documents_read: { maxPerMinute: 300 },
  // Batch reads count as ONE bridge call. Per-doc rate-limiting on the
  // batch payload (max 50 ids per call) is enforced server-side.
  vault_documents_read_many: { maxPerMinute: 60 },
  vault_documents_download_url: { maxPerMinute: 300 },
  vault_documents_get_urls: { maxPerMinute: 60 },
  vault_documents_write: { maxPerMinute: 60 },
  // Composite binary upload (init→PUT→finalize host-side) — heavier than a
  // text write, so a tighter cap. Backend quota still applies (defense-in-depth).
  vault_documents_upload: { maxPerMinute: 30 },
  vault_documents_update: { maxPerMinute: 60 },
  vault_documents_delete: { maxPerMinute: 30 },
  arche_assets_list: { maxPerMinute: 600 },
  arche_assets_index: { maxPerMinute: 120 },
  arche_assets_get_url: { maxPerMinute: 300 },
};

/**
 * Per-(entityId, verb) rate limits for the generic `entity_invoke` op (ADR-012
 * Phase L, Job 3). When a plugin built against the migrated SDK reaches
 * storage / datastore / workspaces / store through `entity_invoke`, its per-verb
 * cap must match what the legacy alias op had — otherwise the four families
 * collapse into the single `entity_invoke` bucket (60/min) and e.g. storage
 * reads drop from 300 to 60. Values mirror the alias entries in RATE_LIMITS
 * above (which mirror the server-side DATA_STORE_RATE_LIMITS). Verbs absent
 * here (datastore `search` — which had no client alias cap — and `subscribe`,
 * plus every AI/text entity) fall back to the generic `entity_invoke` cap.
 */
/**
 * Per-verb rate WEIGHTS (ADR-016 Phase A): a listed verb consumes one unit of
 * its per-minute budget per started `opsPerUnit` operations in its payload,
 * instead of a flat one unit per call. Config-shaped (entity → verb →
 * divisor) rather than a hardcoded entity-ID conditional, so a second
 * weighted verb is a map entry, not another branch. Mirrors
 * DATASTORE_LIMITS.BATCH_OPS_PER_RATE_UNIT server-side; the server's
 * checkDataStoreRateLimit is authoritative — this mirror exists so plugin
 * authors get the friendly local error at the same threshold.
 */
const ENTITY_INVOKE_RATE_WEIGHTS: Record<string, Record<string, number>> = {
  [PLUGIN_DATASTORE_ENTITY_ID]: { batch: 25 },
};

const ENTITY_INVOKE_RATE_LIMITS: Record<string, Record<string, number>> = {
  [PLUGIN_STORAGE_ENTITY_ID]: { read: 300, write: 120, list: 60, delete: 60 },
  [PLUGIN_DATASTORE_ENTITY_ID]: {
    create_collection: 10,
    list_collections: 60,
    delete_collection: 10,
    put: 120,
    get: 300,
    query: 60,
    delete: 60,
    batch: 30,
  },
  [PLUGIN_WORKSPACES_ENTITY_ID]: {
    create: 20,
    list: 120,
    get: 120,
    archive: 20,
    list_members: 120,
    add_member: 30,
    update_member: 30,
    remove_member: 30,
  },
  // Consented USER-vault reads. Mirrors the server: the entity carries a
  // 30/min budget for the family, and `get_consented_document_bytes` — which
  // can serve 15 MB per call — has its own tighter server-side bucket
  // (`vault_consented_bytes`). Without these entries the whole family fell
  // back to the generic 60/min `entity_invoke` bucket, so the client waved
  // through calls the server then refused, which reads to a plugin author as
  // a flaky platform rather than a budget they exceeded.
  [VAULT_USER_DOCUMENTS_ENTITY_ID]: {
    list_consented_documents: 30,
    get_consented_document: 30,
    get_consented_document_url: 30,
    get_consented_document_bytes: 10,
    // Saves to a document in the USER'S files. The server keeps ONE 12/min
    // bucket for the body-lane save and the presigned lane's init together
    // (`vault_consented_save`), so these verbs draw on ONE host bucket too —
    // see ENTITY_INVOKE_SHARED_VERB_BUCKETS. (Splitting it 6 + 6 refused an
    // editor using a single lane at half the server's rate: autosave every
    // 10 s plus a save on close is 7 a minute.) Finalize has no bucket of its
    // own on the server and is only ever driven by the host, paired with init.
    save_consented_document_content: 12,
    init_consented_document_save: 12,
    finalize_consented_document_save: 12,
    // Approved-WORKSPACE reads (ADR-022). The bytes read draws on the SAME
    // server bucket as the consented bytes read (`vault_consented_bytes`), so
    // it shares the host bucket too (ENTITY_INVOKE_SHARED_VERB_BUCKETS); the
    // signed URL has its own 20/min (`vault_workspace_url`).
    list_workspace_documents: 30,
    get_workspace_document: 30,
    get_workspace_document_url: 20,
    get_workspace_document_bytes: 10,
  },
  // Arche-OWNED document operations. Mirrors the server: the byte-read verb's
  // own 10/min bucket (`vault_own_document_bytes`) and the entity-level
  // 60/min (config `rateLimitPerMinute`, sized for the in-place save's
  // autosave cadence), so the host refuses what the server would refuse
  // instead of waving it through to a confusing server-side RATE_LIMIT.
  //
  // EVERY verb of this entity needs an entry: `resolveRateLimit` falls back
  // to the generic 60/min `entity_invoke` bucket on a miss — the exact
  // fail-open the comment above this block describes.
  //
  // Known imprecision: these keys are per-VERB (`entity_invoke:${entityId}:${op}`)
  // while the server's 60 is the entity TOTAL (reads count against it too),
  // so a plugin mixing many reads with saves in one minute can still meet a
  // server RATE_LIMIT — which is why the SDK doc tells callers to surface
  // that error verbatim rather than retry. Fixing it needs a shared client
  // bucket per entity; until then the server remains the real gate.
  [VAULT_ARCHE_DOCUMENTS_ENTITY_ID]: {
    get_own_document_bytes: 10,
    // 50, not the server's 60: these keys are per-VERB while the server's
    // bucket is the entity TOTAL, so setting this to the total would let the
    // host grant 60 saves PLUS 10 reads = 70 against a server total of 60 —
    // i.e. wave through calls the server then refuses, which is the exact
    // failure this block exists to prevent. 50 + 10 leaves the two verbs
    // summing to the server budget. Resize BOTH sides together. (A third and
    // fourth verb have since landed — see ENTITY_INVOKE_ENTITY_TOTALS below,
    // which is the shared per-entity bucket this comment asked for.)
    save_own_document_content: 50,
    // Presigned in-place lane. init mirrors the server's own per-verb bucket
    // (`vault_own_document_save_init`); finalize pairs with it. The normal
    // caller is the HOST (`handleVaultUpload`'s save branch, which charges
    // these via `resolveRateLimit`), not the iframe — the iframe cannot PUT.
    init_own_document_save: 10,
    finalize_own_document_save: 10,
  },
  [PLUGIN_STORE_ENTITY_ID]: {
    get_products: 60,
    purchase: 10,
    get_entitlements: 60,
    get_purchase_history: 30,
    restore: 10,
    cancel_subscription: 10,
  },
};

const VAULT_USER_DOCUMENTS_EDIT_PERMISSION: PluginPermission = 'vault:user-documents:edit';

/** An in-place save aimed at a document in the USER'S files. */
function isUserFilesInPlaceSave(msg: { type: string; payload?: unknown }): boolean {
  if (msg.type !== 'vault_documents_upload') return false;
  const p = msg.payload as { saveToDocumentId?: unknown; saveTarget?: unknown } | null | undefined;
  return typeof p?.saveToDocumentId === 'string' && p.saveTarget === 'user';
}

/**
 * The two in-place save lanes the host can drive. Same choreography (init →
 * presigned PUT → finalize); different entity, verbs and authorization:
 *   - own  — a document the arche created, still arche-private;
 *   - user — a document in the USER'S files the arche holds EDIT access to.
 * Anything but the literal 'user' is the own lane: an unknown value must
 * never be read as the lane that reaches the user's files.
 */
function inPlaceSaveLane(saveTarget: unknown): {
  entityId: string;
  initOp: string;
  finalizeOp: string;
  isUserLane: boolean;
} {
  return saveTarget === 'user'
    ? {
        entityId: VAULT_USER_DOCUMENTS_ENTITY_ID,
        initOp: 'init_consented_document_save',
        finalizeOp: 'finalize_consented_document_save',
        isUserLane: true,
      }
    : {
        entityId: VAULT_ARCHE_DOCUMENTS_ENTITY_ID,
        initOp: 'init_own_document_save',
        finalizeOp: 'finalize_own_document_save',
        isUserLane: false,
      };
}

/**
 * Backoffs before the finalize retries of an in-place save. The throttle it
 * answers is a PER-MINUTE bucket and the bridge exposes no retry-after, so a
 * single short wait nearly always met the same refusal — and the user lost a
 * save whose bytes were already uploaded. Two tries, the second long enough
 * for a rolling window to have moved; still short enough that the editor's
 * "saving…" is not left hanging.
 */
const FINALIZE_RATE_LIMIT_RETRY_DELAYS_MS = [2_000, 8_000] as const;
/** Finalize refusals that leave the staged upload in place (see the retry loop). */
const FINALIZE_RETRYABLE_CODES: ReadonlySet<string> = new Set(['RATE_LIMIT', 'DOCUMENT_BUSY']);

/** Mirrors the server's `MAX_OWN_DOCUMENT_PRESIGNED_SAVE_BYTES` — the
 *  in-place save cap, pinned to the byte-READ cap so a save can be reopened. */
const MAX_IN_PLACE_SAVE_BYTES = 15 * 1024 * 1024;

/**
 * Entity-TOTAL mirror, for entities whose server bucket is shared by every
 * verb (`checkIntegrationEntityRateLimit` keys on the entity, not the verb).
 * Per-verb caps alone cannot mirror that once their sum exceeds the total:
 * the host would wave through calls the server then refuses. A call must fit
 * BOTH its per-verb bucket and this one.
 */
/**
 * Verbs the server meters in ONE named bucket rather than one each. The host
 * keys them the same way, so the mirror refuses exactly what the server would:
 * the per-verb cap above is then the shared bucket's size, and it must be the
 * same number for every verb that names the bucket.
 */
const ENTITY_INVOKE_SHARED_VERB_BUCKETS: Record<string, Record<string, string>> = {
  [VAULT_USER_DOCUMENTS_ENTITY_ID]: {
    save_consented_document_content: 'vault_consented_save',
    init_consented_document_save: 'vault_consented_save',
    get_consented_document_bytes: 'vault_consented_bytes',
    get_workspace_document_bytes: 'vault_consented_bytes',
  },
};

/** Limiter key for one verb: its shared bucket when the server has one. */
function entityVerbRateKey(entityId: string, op: string): string {
  const shared = ENTITY_INVOKE_SHARED_VERB_BUCKETS[entityId]?.[op];
  return `entity_invoke:${entityId}:${shared ?? op}`;
}

const ENTITY_INVOKE_ENTITY_TOTALS: Record<string, number> = {
  [VAULT_ARCHE_DOCUMENTS_ENTITY_ID]: 60,
  [VAULT_USER_DOCUMENTS_ENTITY_ID]: 30,
};

/** Burst detection: catches runaway loops (e.g., useEffect calling dataStore.put every render). */
interface BurstBucket {
  count: number;
  windowStart: number;
  /** When set, all calls of this type are blocked until this timestamp. */
  blockedUntil?: number;
}

const BURST_THRESHOLD = 50;
const BURST_WINDOW_MS = 5_000;
const BURST_COOLDOWN_MS = 10_000;

/**
 * Extracts a short diagnostic identifier from a bridge call's payload so that
 * rate-limit and runaway-loop errors can point to the specific path/resource
 * being hammered (e.g. `__state/gameState` when a game loop is spamming
 * `usePersistentState` writes). Returns an empty string for call types where
 * no sensible target exists.
 */
function describeBridgeTarget(type: string, payload: unknown): string {
  if (!payload || typeof payload !== 'object') return '';
  const p = payload as Record<string, unknown>;

  // Retired op-family aliases (storage_*/data_*) can still arrive from old,
  // immutable bundles; describe their target so burst/rate-limit logs stay
  // actionable. Uses if/string-compare, NOT `case` labels, so bridge-op-freeze
  // (which scans this file's `case '<op>':` labels) sees no phantom bridge ops.
  if (type === 'storage_read' || type === 'storage_write' || type === 'storage_delete') {
    return typeof p.path === 'string' ? `path: ${p.path}` : '';
  }
  if (type === 'storage_list') {
    return typeof p.prefix === 'string' ? `prefix: ${p.prefix}` : '';
  }
  if (type === 'data_put' || type === 'data_get' || type === 'data_delete') {
    const collection = typeof p.collection === 'string' ? p.collection : '';
    const docId = typeof p.docId === 'string' ? p.docId : '';
    if (collection && docId) return `${collection}/${docId}`;
    return collection ? `collection: ${collection}` : '';
  }
  if (
    type === 'data_query' ||
    type === 'data_create_collection' ||
    type === 'data_delete_collection'
  ) {
    return typeof p.collection === 'string' ? `collection: ${p.collection}` : '';
  }

  switch (type) {
    case 'entity_invoke':
    case 'client_entity_invoke':
      return typeof p.entityId === 'string' ? `entity: ${p.entityId}` : '';
    case 'navigate':
      return typeof p.path === 'string' ? `path: ${p.path}` : '';
    case 'open_arche':
      return typeof p.archeId === 'string'
        ? `arche: ${p.archeId}${typeof p.path === 'string' ? `/${p.path}` : ''}`
        : '';
    case 'vault_documents_read':
    case 'vault_documents_download_url':
    case 'vault_documents_update':
    case 'vault_documents_delete':
      return typeof p.documentId === 'string' ? `documentId: ${p.documentId}` : '';
    case 'vault_documents_write':
      return typeof p.name === 'string' ? `name: ${p.name}` : '';
    case 'vault_documents_list':
      return typeof p.search === 'string' ? `search: ${p.search}` : '';
    default:
      return '';
  }
}

/**
 * Retired op-family aliases (ADR-012 Phase L Job 3 Step 2). New bundles emit
 * `entity_invoke`; only old, immutable published bundles still emit these bare
 * names. `data_subscribe` (realtime) is NOT retired — excluded via lookahead.
 */
const RETIRED_ALIAS_OP_RE = /^(?:storage_|workspace_|store_|data_(?!subscribe))/;

// Thrown by checkBurst when a call is blocked for runaway-loop reasons. The
// burst path already fires onLog on the first breach and deliberately stays
// silent during cooldown — the outer request-failure catch uses this tag to
// skip its own onLog so we don't double-log the breach and re-log every
// cooldown-blocked call.
class BridgeBurstError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BridgeBurstError';
  }
}

/**
 * Error carrying the backend bridge's machine-readable failure code from a
 * host-side fetch to the iframe-bound error response. `handleMessage`'s catch
 * forwards `code` as `errorCode` on the posted response message, where the
 * SDK reconstructs a `FiasBridgeError` with the same code — so plugin code
 * sees a real Error with `.message` + `.code` instead of the opaque `{}`
 * that `JSON.stringify(new Error(...))` yields (the Rota incident). The wire
 * format stays plain string fields; the Error never crosses postMessage.
 */
class HostBridgeError extends Error {
  code: string;
  /**
   * The server's correlation id for the failed call, when it sent one — the
   * id on the gateway's audit row and the log line. Forwarded as
   * `errorRequestId` so a plugin can show it as a support reference.
   */
  requestId?: string;

  constructor(message: string, code: string, requestId?: string) {
    super(message);
    this.name = 'HostBridgeError';
    this.code = code;
    if (requestId) this.requestId = requestId;
  }
}

/** `requestId` from a backend `{ error: { requestId } }` body, when present. */
function errorRequestIdOf(body: unknown): string | undefined {
  const id = (body as { error?: { requestId?: unknown } } | null)?.error?.requestId;
  return typeof id === 'string' && id ? id : undefined;
}

/**
 * An AI `entity_invoke` answered 413: the request body is over the bridge's
 * limit (256 KB for a text call, 1.5 MB with images). The server reports that
 * as a generic validation error; named here so the plugin can tell it apart —
 * `entity_invoke` has no other 413. Current SDKs refuse such a call before
 * sending it; this covers plugins built against an older one.
 */
function requestTooLargeError(): HostBridgeError {
  return new HostBridgeError(
    'The request is larger than the platform accepts (256 KB for a text call, 1.5 MB with ' +
      'images). Send less history or a shorter input.',
    'REQUEST_TOO_LARGE',
  );
}

/**
 * Turns an SSE `{ error: … }` frame into a `HostBridgeError`.
 *
 * Two frame shapes are in play. The preview route emits the same structured
 * body as its JSON responses (`{ error: { code, message, requestId, … } }`),
 * so a mid-stream failure keeps its machine-readable code. The published
 * bridge still emits a bare `{ error: "<message>" }` string; it is accepted
 * here so the two readers can share one code path, and so the published frame
 * can gain structure later without a client change.
 */
function hostBridgeErrorFromSseFrame(raw: unknown): HostBridgeError {
  if (raw && typeof raw === 'object') {
    const e = raw as { code?: unknown; message?: unknown; requestId?: unknown };
    return new HostBridgeError(
      typeof e.message === 'string' && e.message ? e.message : 'AI invocation failed',
      typeof e.code === 'string' && e.code ? e.code : 'BRIDGE_ERROR',
      typeof e.requestId === 'string' && e.requestId ? e.requestId : undefined,
    );
  }
  return new HostBridgeError(
    typeof raw === 'string' && raw ? raw : 'AI invocation failed',
    'BRIDGE_ERROR',
  );
}

/**
 * Async-surface poll-fallback cadence (image_generate + surface_invoke).
 *
 * The WebSocket broadcast / late-subscribe replay is the primary, fast path —
 * it resolves the moment the result lands. This poll only covers the case
 * where BOTH the live broadcast was missed AND the subscription was already
 * open when the (now-recoverable) server-side completion happened, so the
 * one-shot replay never re-fires for this connection.
 *
 * Cadence: stay quiet through the typical generation window (no polls on the
 * happy path, where WS wins first), then — if still unresolved — poll on a
 * short interval so a missed broadcast recovers in seconds, not the old 180 s.
 * The server's `request_state` row makes these polls cross-replica durable.
 */
const ASYNC_POLL_GRACE_MS = 15_000; // first poll after the typical gen window
const ASYNC_POLL_INTERVAL_MS = 5_000; // then every 5 s
const ASYNC_POLL_MAX_MS = 180_000; // hard ceiling (unchanged safety bound)
/**
 * The ceiling for `image_generate`, longer than the surfaces': a high-quality
 * GPT Image render runs past two minutes. It sits above the server's own
 * provider timeout (`OPENAI_IMAGE_TIMEOUT_MS`, 300 s) and below the SDK's
 * (`IMAGE_GENERATE_TIMEOUT_MS`, 320 s), so a slow image is delivered and a
 * genuinely stuck one is reported by the host, not by an SDK timeout. Pinned
 * by `tests/architecture/image-generate-deadlines.test.ts`.
 */
const IMAGE_POLL_MAX_MS = 310_000;

/**
 * Permission requirements per bridge call type.
 */
const PERMISSION_MAP: Record<string, PluginPermission> = {
  get_user: 'user:profile:read',
  get_theme: 'theme:read',
  image_generate: 'entities:image_generate',
  entities_list_image: 'entities:image_generate',
  audio_generate: 'entities:audio_generate',
  // `entity_invoke` deliberately omitted (ADR-012 Phase M4): its permission
  // is path-dependent — integration entities are gated by their
  // config-declared permission (mirrored in
  // `lib/integration-entity-permissions.ts`), everything else by
  // `entities:invoke`. Resolved at request time inside the case block via
  // `entityInvokeRequiredPermission`, matching the server bridges.
  // `client_entity_invoke` deliberately omitted: the required permission
  // is per-entity (resolved from `pluginInvocable.pluginPermission` at
  // request time inside the case block), so the centralized map can't
  // answer statically.
  vault_documents_pick: 'vault:user-documents:read',
  vault_documents_list: 'vault:documents:read',
  vault_documents_read: 'vault:documents:read',
  vault_documents_read_many: 'vault:documents:read',
  vault_documents_download_url: 'vault:documents:read',
  vault_documents_search: 'vault:documents:read',
  vault_documents_get_urls: 'vault:documents:read',
  vault_documents_write: 'vault:documents:write',
  vault_documents_update: 'vault:documents:write',
  vault_documents_delete: 'vault:documents:write',
  vault_documents_attach: 'vault:documents:write',
  vault_documents_detach: 'vault:documents:write',
  vault_documents_upload_init: 'vault:documents:write',
  vault_documents_upload_finalize: 'vault:documents:write',
  vault_documents_upload: 'vault:documents:write',
  arche_assets_list: 'assets:read',
  arche_assets_index: 'assets:read',
  arche_assets_get_url: 'assets:read',
  open_arche: 'navigation:open_arche',
};

export class PluginBridgeHost {
  private options: PluginBridgeOptions;
  /**
   * Live permission set enforced by every capability gate. Initialized from
   * `options.permissions` and updatable via `updatePermissions()` — the
   * builder preview edits fias-plugin.json without rebuilding the preview
   * HTML (the manifest isn't bundled), so the identical-HTML guard skips the
   * iframe reload and this host instance survives the manifest change. A
   * construction-time snapshot then kept enforcing the STALE set ("Permission
   * denied: entities:invoke not granted" after the manifest was fixed — the
   * Gym/text-to-video incidents). Published mode never calls
   * `updatePermissions()`; its JWT capability-token snapshot semantics are by
   * design.
   */
  private permissions: PluginPermission[];
  private rateLimits = new Map<string, RateLimitBucket>();
  private burstBuckets = new Map<string, BurstBucket>();

  private initRetryTimers: ReturnType<typeof setTimeout>[] = [];

  /**
   * Whether the connected plugin's SDK advertised preview-state support in its
   * `ready` message. Gates capture/restore so older SDKs (and non-preview
   * surfaces) are unaffected and pay no latency.
   */
  private pluginSupportsPreviewState = false;
  /** One-shot guard so a repeated `ready` (e.g. StrictMode) doesn't re-restore. */
  private hasRestored = false;
  /** Resolver for an in-flight captureState() awaiting the plugin's reply. */
  private pendingStateCapture: {
    resolve: (snapshot: unknown) => void;
    timer: ReturnType<typeof setTimeout>;
  } | null = null;

  /** Timestamp of the last APPLIED ai_actions_declare (coalescing window base). */
  private lastAiDeclareAt = 0;

  /** Timestamp of the last honoured `ai_open` request (debounce base). */
  private lastAiOpenAt = 0;
  /** Declaration held for the window edge (trailing-edge coalescing; latest wins). */
  private heldAiDeclaration: {
    declaration: PluginRuntimeAiDeclaration;
    timer: ReturnType<typeof setTimeout>;
  } | null = null;
  /** Timestamp of the last APPLIED ai_state snapshot. */
  private lastAiStateAt = 0;
  /** Snapshot held for the window edge (trailing-edge coalescing; latest wins). */
  private heldAiState: {
    state: Record<string, unknown>;
    timer: ReturnType<typeof setTimeout>;
  } | null = null;
  /** Monotonic id source for ai_action_dispatch message ids. */
  private aiDispatchSeq = 0;
  /**
   * In-flight ai_action dispatches awaiting the plugin's `ai_action_result`,
   * keyed by messageId. The assistant dispatches one ui_action at a time, but
   * a Map keeps a straggler reply from a timed-out dispatch from being
   * mistaken for the current one.
   */
  /** True once the plugin has answered `init` with `ready` — i.e. its SDK
   *  is listening. One-way host→plugin pushes must gate on this, not on the
   *  iframe `load` event, which fires before the plugin's listener exists. */
  private pluginReady = false;
  /** Whether the plugin's SDK advertised `arche_handoff` support on `ready`. */
  private pluginSupportsHandoff = false;
  private pendingAiDispatches = new Map<
    string,
    {
      resolve: () => void;
      reject: (err: Error) => void;
      timer: ReturnType<typeof setTimeout>;
    }
  >();

  /**
   * In-flight WS-async bridge requests (today: `image_generate`).
   *
   * Each entry holds the Promise resolver pair that completes the
   * outer `handleRequest` Promise, plus a cleanup callback that
   * unsubscribes the WS listeners and clears the safety timeout. Keyed
   * by the server-issued `requestId` so the WS callbacks (which fire
   * for every in-flight subscription the user owns) can filter to the
   * matching entry and ignore broadcasts for other plugins/contexts.
   *
   * Cleanup runs on EVERY terminal path — WS success, WS error,
   * safety-timeout fallback, host `destroy()` — so listeners cannot
   * leak across plugin sessions. See `plan §"Cleanup contract"`.
   */
  private pendingImageRequests = new Map<
    string,
    {
      resolve: (value: unknown) => void;
      reject: (err: Error) => void;
      cleanup: () => void;
    }
  >();

  /**
   * In-flight WS-async `surface_invoke` requests (surfaces with
   * `asyncDispatch`). Same contract as `pendingImageRequests` — keyed by
   * the server `requestId`, torn down on every terminal path.
   */
  private pendingSurfaceRequests = new Map<
    string,
    {
      resolve: (value: unknown) => void;
      reject: (err: Error) => void;
      cleanup: () => void;
    }
  >();

  constructor(options: PluginBridgeOptions) {
    this.options = options;
    this.permissions = [...options.permissions];
    this.handleMessage = this.handleMessage.bind(this);
    window.addEventListener('message', this.handleMessage);
  }

  /**
   * Replace the enforced permission set in place (preview mode: the manifest
   * changed but the preview HTML — and therefore this host instance — did
   * not). Re-sends `init` so the plugin-side SDK's view of its permissions
   * refreshes too; the SDK ignores duplicate init after the first, so the
   * refresh is best-effort for the plugin but authoritative for the host's
   * own gates, which read the live field on every request.
   */
  updatePermissions(perms: PluginPermission[]): void {
    this.permissions = [...perms];
    this.sendInit();
  }

  /**
   * Replace the preview store catalog in place (preview mode: the manifest's
   * `products` changed but the preview HTML did not — same surviving-bridge
   * situation as updatePermissions). The store stubs read
   * `options.previewStoreProducts` on every call, so a live replace is all
   * that's needed.
   */
  updatePreviewStoreProducts(
    products: NonNullable<PluginBridgeOptions['previewStoreProducts']>,
  ): void {
    this.options.previewStoreProducts = [...products];
  }

  /**
   * Lazily-built manager for subscribe-kind entity verbs (ADR-012 Phase M4).
   * Lives in its own module — the freeze/coverage arch tests scan this
   * file's `case` labels, and the manager is deliberately entity-agnostic
   * (envelope-shape detection, never entity-ID switches).
   */
  private subscriptionManager: PluginSubscriptionManager | null = null;
  private subscriptionEventCounter = 0;

  private getSubscriptionManager(): PluginSubscriptionManager {
    if (!this.subscriptionManager) {
      this.subscriptionManager = new PluginSubscriptionManager({
        // Host-initiated re-handshakes (renewal/reconnect) bypass the
        // iframe-facing rate limiter on purpose; the server's
        // data_subscribe bucket is authoritative.
        invoke: (payload) => this.callBackendOp('entity_invoke', payload),
        socket: getWebSocket(WEBSOCKET_ENDPOINT),
        getSocketTicket: () => authApi.getWebSocketTicket(),
        sendToPlugin: (message) => {
          const { type, ...payload } = message as { type: string } & Record<string, unknown>;
          this.sendToPlugin({
            type,
            messageId: `sub_${++this.subscriptionEventCounter}`,
            payload,
          });
        },
      });
    }
    return this.subscriptionManager;
  }

  /**
   * Send the init message to the plugin iframe to establish the bridge.
   *
   * When loading via srcDoc, the iframe's onLoad fires before ES module
   * scripts (loaded from esm.sh) have executed. The SDK's message listener
   * may not exist yet, so we retry init a few times with increasing delays.
   * The SDK ignores duplicate init messages after the first.
   */
  init(): void {
    this.sendInit();
    this.initRetryTimers = [
      setTimeout(() => this.sendInit(), 200),
      setTimeout(() => this.sendInit(), 1000),
      setTimeout(() => this.sendInit(), 3000),
    ];
  }

  /**
   * The plugin-relative path the plugin has most recently been TOLD it is at
   * (via `init` or a `navigate_update`). Lets `sendNavigationUpdate` no-op
   * when the plugin's view is already correct, so the renderer can fire it on
   * every host route change without thinking about which ones originated in
   * the plugin.
   */
  private lastSentPath: string | null = null;

  /**
   * The platform URL prefix this plugin's pages live under RIGHT NOW.
   *
   * Plugin arches are routed by SLUG across the app (`archePath`), so the
   * segment in the address bar is normally the slug, not the canonical
   * `arc_<32hex>` id. Resolving the plugin's own `navigateTo('/x')` against
   * the canonical id would REWRITE that segment mid-session — and a change to
   * the `:id` route param makes ConditionalProtectedRoute drop its children
   * and refetch metadata, which destroys this iframe and restarts the plugin
   * on its home screen. That is the "first click reloads the plugin and does
   * nothing, the second click works" bug. Staying on whatever segment the user
   * arrived under keeps the param — and therefore the iframe — stable.
   *
   * Falls back to the canonical id when the host did not supply a segment
   * (the builder/dev previews), which is the pre-existing behaviour.
   */
  private ownUrlPrefix(): string {
    const segment = this.options.getUrlSegment?.();
    return segment ? `/a/${segment}` : `/a/${this.options.archId}`;
  }

  /**
   * The current address-bar path expressed in the PLUGIN's own route space —
   * the same space `navigateTo` accepts, so a plugin can round-trip what it
   * navigated to. `/a/<segment>/my-books` is handed over as `/my-books`, and a
   * path that is not under this plugin at all (the previews, where the plugin
   * is embedded in someone else's page) is reported as its home, `/`.
   */
  private currentPluginPath(): string {
    const prefix = this.ownUrlPrefix();
    const pathname = window.location.pathname;
    if (pathname === prefix) return '/';
    if (pathname.startsWith(`${prefix}/`)) return pathname.slice(prefix.length);
    return '/';
  }

  private sendInit(): void {
    const currentPath = this.currentPluginPath();
    this.lastSentPath = currentPath;
    this.sendToPlugin({
      type: 'init',
      messageId: 'init_0',
      payload: {
        archId: this.options.archId,
        permissions: this.permissions,
        theme: this.options.getTheme(),
        // Vendored-library asset URLs — only for plugins that declared the
        // capability, so ordinary plugins never see CDN internals in their init
        // payload.
        vendoredAssets:
          this.permissions.includes('sandbox:vendored-libraries') && this.options.getVendoredAssets
            ? this.options.getVendoredAssets()
            : null,
        // The text-model catalog — only for plugins that can invoke a model.
        // Init-payload data (a transport exception): nothing to invoke or
        // meter; `entity_invoke` still resolves and bills server-side.
        textModels: this.canInvokeTextModels() ? (this.options.getTextModels?.() ?? null) : null,
        currentPath,
        // Protocol-version handshake (see HOST_BRIDGE_PROTOCOL_VERSION
        // block above). The SDK rejects hosts older than its required
        // minimum; the host rejects plugins older than ours via the
        // `ready` message handler below.
        hostProtocolVersion: HOST_BRIDGE_PROTOCOL_VERSION,
        minSdkProtocolVersion: HOST_REQUIRES_SDK_PROTOCOL_VERSION,
      },
    });
  }

  private canInvokeTextModels(): boolean {
    return this.permissions.includes('entities:invoke');
  }

  /**
   * Push the text-model catalog to the plugin once the host has loaded it (the
   * SDK ignores a repeated `init`, so a catalog that arrives after init needs
   * its own message). A no-op for plugins without `entities:invoke`.
   */
  sendTextModelsUpdate(): void {
    const models = this.options.getTextModels?.();
    if (!this.canInvokeTextModels() || !models) return;
    this.sendToPlugin({
      type: 'text_models_update',
      messageId: `text_models_${Date.now()}`,
      payload: models,
    });
  }

  /**
   * Notify the plugin of a theme change.
   */
  sendThemeUpdate(): void {
    this.sendToPlugin({
      type: 'theme_update',
      messageId: `theme_${Date.now()}`,
      payload: this.options.getTheme(),
    });
  }

  /**
   * Tell the plugin the host's path changed — browser back/forward, or any
   * host-side navigation while the plugin stays mounted. Without this a
   * plugin's `currentPath` is frozen at whatever `init` reported, so the back
   * button moves the address bar and leaves the plugin showing the old page.
   *
   * Safe to call on every route change: it no-ops when the plugin already
   * knows this path, which covers both the mount-time call and the echo of
   * the plugin's own `navigateTo` coming back around through the host.
   */
  sendNavigationUpdate(): void {
    const path = this.currentPluginPath();
    if (path === this.lastSentPath) return;
    this.lastSentPath = path;
    this.sendToPlugin({
      type: 'navigate_update',
      messageId: `nav_${Date.now()}`,
      payload: path,
    });
  }

  /**
   * Navigate the plugin to a specific step/node.
   */
  navigateToStep(nodeId: string): void {
    this.sendToPlugin({
      type: 'step_navigate',
      messageId: `step_${Date.now()}`,
      payload: { nodeId },
    });
  }

  /**
   * Ask the running plugin for a serializable snapshot of its transient UI
   * state, so the host can restore it after a preview rebuild reloads the
   * iframe (which otherwise destroys that state — e.g. a generated report).
   *
   * Resolves with the snapshot (whatever the plugin returns) or `null` if the
   * plugin doesn't support the protocol or doesn't reply within
   * `PREVIEW_STATE_CAPTURE_TIMEOUT_MS`. Never rejects — a failed capture must
   * not block the rebuild.
   */
  captureState(): Promise<unknown> {
    if (!this.pluginSupportsPreviewState) return Promise.resolve(null);
    // A previous capture that's still pending is superseded — resolve it null.
    if (this.pendingStateCapture) {
      clearTimeout(this.pendingStateCapture.timer);
      this.pendingStateCapture.resolve(null);
      this.pendingStateCapture = null;
    }
    return new Promise<unknown>((resolve) => {
      const finish = (snapshot: unknown) => {
        if (this.pendingStateCapture) {
          clearTimeout(this.pendingStateCapture.timer);
          this.pendingStateCapture = null;
        }
        resolve(snapshot);
      };
      const timer = setTimeout(() => finish(null), PREVIEW_STATE_CAPTURE_TIMEOUT_MS);
      this.pendingStateCapture = { resolve: finish, timer };
      this.sendToPlugin({
        type: 'preview_state_capture',
        messageId: `pscap_${Date.now()}`,
        payload: {},
      });
    });
  }

  /**
   * Apply a parsed runtime declaration now, or hold it for the coalescing
   * window's edge (latest wins). A held declaration is NEVER dropped — the
   * content-diffed SDK will not re-send it, so a drop here would strand the
   * plugin on stale verbs until its next unrelated change.
   */
  private applyOrHoldAiDeclaration(declaration: PluginRuntimeAiDeclaration): void {
    if (this.heldAiDeclaration) {
      // Newer declaration supersedes the held one; the pending timer stands.
      this.heldAiDeclaration.declaration = declaration;
      return;
    }
    const now = Date.now();
    const remaining = AI_ACTIONS_MIN_REDECLARE_INTERVAL_MS - (now - this.lastAiDeclareAt);
    if (remaining <= 0) {
      this.lastAiDeclareAt = now;
      this.options.onAiActionsDeclare?.(declaration);
      return;
    }
    const holder = {
      declaration,
      timer: setTimeout(() => {
        this.heldAiDeclaration = null;
        this.lastAiDeclareAt = Date.now();
        this.options.onAiActionsDeclare?.(holder.declaration);
      }, remaining),
    };
    this.heldAiDeclaration = holder;
  }

  private cancelHeldAiDeclaration(): void {
    if (this.heldAiDeclaration) {
      clearTimeout(this.heldAiDeclaration.timer);
      this.heldAiDeclaration = null;
    }
  }

  /** ai_state twin of applyOrHoldAiDeclaration — trailing edge, latest wins. */
  private applyOrHoldAiState(state: Record<string, unknown>): void {
    if (this.heldAiState) {
      this.heldAiState.state = state;
      return;
    }
    const now = Date.now();
    const remaining = AI_STATE_MIN_INTERVAL_MS - (now - this.lastAiStateAt);
    if (remaining <= 0) {
      this.lastAiStateAt = now;
      this.options.onAiState?.(state);
      return;
    }
    const holder = {
      state,
      timer: setTimeout(() => {
        this.heldAiState = null;
        this.lastAiStateAt = Date.now();
        this.options.onAiState?.(holder.state);
      }, remaining),
    };
    this.heldAiState = holder;
  }

  /**
   * Dispatch a Fias-AI-requested action into the plugin and await its result.
   * Host→plugin request/response, mirroring captureState()'s shape but keyed
   * by messageId (a straggler reply from a timed-out dispatch must not settle
   * the current one). Resolves when the plugin replies `{ok: true}`; rejects
   * on `{ok: false}`, on timeout, or on bridge teardown — the rejection is
   * what the assistant loop reports to the model as the action's failure.
   */
  dispatchAiAction(actionId: string, params: Record<string, unknown>): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const messageId = `aiact_${Date.now()}_${++this.aiDispatchSeq}`;
      const timer = setTimeout(() => {
        this.pendingAiDispatches.delete(messageId);
        reject(
          new Error(`the plugin did not report a result within ${AI_ACTION_DISPATCH_TIMEOUT_MS}ms`),
        );
      }, AI_ACTION_DISPATCH_TIMEOUT_MS);
      this.pendingAiDispatches.set(messageId, { resolve, reject, timer });
      this.sendToPlugin({
        type: 'ai_action_dispatch',
        messageId,
        payload: { actionId, params },
      });
    });
  }

  /**
   * Hand the plugin a document to open (Arche Handoff, `open_document`).
   *
   * Deliberately one-way and un-acknowledged, unlike `dispatchAiAction`. The
   * assistant's action dispatch pauses a turn on the result, so a silent drop
   * there strands the model; a handoff has no waiting turn — the plugin owns
   * the outcome and reports it to the user in its own UI, including refusing
   * a file type it can't open. Adding a result channel would only let the
   * host duplicate an error the plugin is already showing.
   *
   * Whether a handoff can be delivered right now. Separate from the send so a
   * caller can decide BEFORE doing preparatory work (minting the read grant)
   * that a refusal would waste. Three outcomes, and they must be told apart:
   *
   *   `'ready'` — go ahead.
   *   `'not-ready'` — the plugin has not answered `init` with `ready` yet, so
   *     its SDK is not listening. Transient: queue and retry on
   *     `onPluginReady`. (The SDK's own buffer covers the smaller window
   *     between `ready` and the hook mounting; it cannot cover this one,
   *     because nothing is there to buffer it.)
   *   `'unsupported'` — the plugin answered `ready` WITHOUT advertising
   *     handoff support, i.e. it was built against an SDK predating the lane.
   *     Terminal: retrying never helps, and posting anyway would be dropped in
   *     silence. The caller tells the user instead.
   */
  handoffReadiness(): 'ready' | 'not-ready' | 'unsupported' {
    if (!this.pluginReady) return 'not-ready';
    if (!this.pluginSupportsHandoff) return 'unsupported';
    return 'ready';
  }

  /** Post a handoff. Check `handoffReadiness()` first — this does not. */
  dispatchHandoff(payload: ArcheHandoffPayload): void {
    this.sendToPlugin({
      type: 'arche_handoff',
      messageId: `handoff_${Date.now()}`,
      payload,
    });
  }

  /**
   * Replay a previously-captured state snapshot to the freshly loaded plugin.
   * Called once the plugin signals readiness (so its SDK and restore handler
   * are live). No-op unless the plugin supports the protocol and an
   * `initialPreviewState` was provided.
   */
  private maybeRestoreState(): void {
    if (this.hasRestored || !this.pluginSupportsPreviewState) return;
    const snapshot = this.options.initialPreviewState;
    if (snapshot === undefined || snapshot === null) return;
    this.hasRestored = true;
    this.sendToPlugin({
      type: 'preview_state_restore',
      messageId: `psrest_${Date.now()}`,
      payload: snapshot,
    });
    this.options.onPreviewStateRestored?.();
  }

  /**
   * Clean up event listeners and release all in-memory state.
   *
   * The per-call Maps (rateLimits, burstBuckets) are bounded by the number
   * of unique message types (~20) so they don't grow unbounded on their
   * own, but preview Maps track plugin-controlled keys. Clearing them here
   * prevents re-mount loops in dev from accumulating state across hosts.
   */
  destroy(): void {
    for (const timer of this.initRetryTimers) clearTimeout(timer);
    this.initRetryTimers = [];
    // Resolve any in-flight state capture so an awaiting onBeforePreviewReload
    // doesn't hang on a torn-down bridge.
    if (this.pendingStateCapture) {
      clearTimeout(this.pendingStateCapture.timer);
      this.pendingStateCapture.resolve(null);
      this.pendingStateCapture = null;
    }
    // Reject in-flight AI action dispatches so the assistant loop's awaiting
    // applyAction settles deterministically instead of riding out its timeout.
    for (const [, entry] of this.pendingAiDispatches) {
      clearTimeout(entry.timer);
      entry.reject(new Error('the plugin was closed before the action completed'));
    }
    this.pendingAiDispatches.clear();
    // Drop coalescing holders — their callbacks must not fire post-teardown.
    this.cancelHeldAiDeclaration();
    if (this.heldAiState) {
      clearTimeout(this.heldAiState.timer);
      this.heldAiState = null;
    }
    // Tear down any in-flight WS-async bridge requests so the iframe's
    // awaiting Promises reject deterministically. The cleanup function
    // unsubs the WS listeners + clears the safety timer; the reject
    // surfaces to the outer `handleRequest` await which posts an error
    // response into the iframe.
    for (const [, entry] of this.pendingImageRequests) {
      entry.cleanup();
      entry.reject(new Error('plugin_unmounted'));
    }
    this.pendingImageRequests.clear();
    for (const [, entry] of this.pendingSurfaceRequests) {
      entry.cleanup();
      entry.reject(new Error('plugin_unmounted'));
    }
    this.pendingSurfaceRequests.clear();
    // Tear down realtime subscriptions: clears renewal/ack/debounce timers,
    // unsubscribes the singleton socket's topics + listeners. Same
    // cleanup-on-every-terminal-path contract as the pending maps above.
    this.subscriptionManager?.destroy();
    this.subscriptionManager = null;
    window.removeEventListener('message', this.handleMessage);
    this.rateLimits.clear();
    this.burstBuckets.clear();
  }

  private async handleMessage(event: MessageEvent): Promise<void> {
    // Only handle messages from our plugin iframe
    if (event.source !== this.options.iframe.contentWindow) return;

    const data = event.data;
    if (!data || typeof data !== 'object' || !data.type) return;

    // Plugin SDK requesting init (handles srcDoc module loading race condition)
    if (data.type === 'request_init') {
      this.sendInit();
      return;
    }

    // All other messages require messageId
    if (!(data as BridgeMessage).messageId) return;

    // Fire-and-forget messages (no response needed)
    if (data.type === 'ready') {
      // Plugin acknowledged init — stop retrying
      for (const timer of this.initRetryTimers) clearTimeout(timer);
      this.initRetryTimers = [];

      // Protocol-version compatibility check. The SDK ≥ v1 sends its
      // `sdkProtocolVersion` in the ready payload. An older SDK omits
      // the field — that's treated as v1 (the first published contract),
      // which is forward-compatible if our minimum is also v1. Once we
      // bump HOST_REQUIRES_SDK_PROTOCOL_VERSION past 1, a missing field
      // becomes "too old" automatically.
      const payload = (data as BridgeMessage).payload as
        | {
            sdkProtocolVersion?: number;
            supportsPreviewState?: boolean;
            supportsArcheHandoff?: boolean;
          }
        | undefined;
      const sdkVersion = payload?.sdkProtocolVersion ?? 1;
      if (sdkVersion < HOST_REQUIRES_SDK_PROTOCOL_VERSION) {
        this.options.onLog?.(
          'warn',
          `Plugin bridge SDK v${sdkVersion} is older than the minimum this host accepts (v${HOST_REQUIRES_SDK_PROTOCOL_VERSION}).`,
        );
        this.options.onIncompatibleSdk?.(sdkVersion, HOST_REQUIRES_SDK_PROTOCOL_VERSION);
      }
      // Preview state preservation is gated on the plugin advertising support,
      // and only meaningful in preview mode. Now that the plugin's SDK is live,
      // replay any snapshot captured before this reload.
      this.pluginSupportsPreviewState =
        this.options.previewMode === true && payload?.supportsPreviewState === true;
      this.maybeRestoreState();
      // The plugin's SDK is now live and listening. Anything the host pushes
      // one-way — a handoff — has to wait for this point, not the iframe's
      // `load` event: `init` is re-sent at 0/200/1000/3000 ms precisely because
      // a postMessage at load time often lands in a frame with no listener yet.
      this.pluginReady = true;
      // Absent on every SDK older than the handoff lane. Fail closed: a
      // handoff such a bundle cannot act on must be REFUSED so the host can
      // say so, not posted into a frame that will ignore it.
      this.pluginSupportsHandoff = payload?.supportsArcheHandoff === true;
      this.options.onPluginReady?.();
      return;
    }

    // Plugin's reply to a captureState() request — its serializable snapshot.
    if (data.type === 'preview_state') {
      let snapshot = (data as BridgeMessage).payload ?? null;
      // Defensive cap: drop an oversized snapshot rather than ferry it across
      // the reload. The report just won't restore — never an OOM.
      if (snapshot !== null) {
        let bytes = 0;
        try {
          bytes = JSON.stringify(snapshot).length;
        } catch {
          // Non-serializable (shouldn't happen post-structured-clone) — drop it.
          bytes = PREVIEW_STATE_MAX_BYTES + 1;
        }
        if (bytes > PREVIEW_STATE_MAX_BYTES) {
          this.options.onLog?.(
            'warn',
            `Preview state snapshot dropped: ${bytes} bytes exceeds the ${PREVIEW_STATE_MAX_BYTES}-byte limit.`,
          );
          snapshot = null;
        }
      }
      this.pendingStateCapture?.resolve(snapshot);
      return;
    }

    if (data.type === 'resize') {
      const { height } = data.payload as { height: number };
      if (typeof height === 'number' && height > 0) {
        this.options.onResize?.(height);
      }
      return;
    }

    if (data.type === 'toast') {
      const { message, variant } = data.payload as { message: string; variant: string };
      if (typeof message === 'string') {
        this.options.onToast?.(message, variant ?? 'info');
      }
      return;
    }

    if (data.type === 'subscription_unsubscribe') {
      // Fire-and-forget release of one logical realtime subscription
      // (ADR-012 Phase M4). Refcounted: the socket topic is torn down only
      // when the last logical subscriber releases. In the if-chain (not a
      // case label) — the freeze/coverage arch tests scan case labels.
      const { subscriptionId } = data.payload as { subscriptionId?: string };
      if (typeof subscriptionId === 'string') {
        this.subscriptionManager?.release(subscriptionId);
      }
      return;
    }

    // Runtime Fias AI action declaration — fire-and-forget metadata
    // registration (client-plane, same family as resize/toast: performs
    // nothing, bills nothing, touches no server). In the if-chain, NOT a
    // case label — the freeze/coverage arch tests scan case labels, and this
    // is transport under the ui_action exception (docs/capability-placement.md).
    // The lane is unmetered (checkRateLimit runs after this chain), so every
    // bound is enforced right here. INVALID declarations are dropped with a
    // warn; VALID ones arriving too fast are trailing-edge coalesced (latest
    // wins, applied at the window edge) — a legitimate declaration must never
    // be silently lost, because the content-diffed SDK will not re-send it.
    if (data.type === 'ai_actions_declare') {
      if (!this.permissions.includes('ai:actions')) {
        this.options.onLog?.(
          'warn',
          'ai_actions_declare ignored: "ai:actions" permission not granted',
        );
        return;
      }
      const payload = (data as BridgeMessage).payload;
      if (payload === null) {
        // Explicit clear of a previous declaration — always allowed and
        // immediate: cancel anything held, reset the throttle so a
        // clear→declare pair (React StrictMode's unmount/remount of
        // useFiasAIActions) applies without waiting out a window.
        this.cancelHeldAiDeclaration();
        this.lastAiDeclareAt = 0;
        this.options.onAiActionsDeclare?.(null);
        return;
      }
      const parsed = parseRuntimeAiDeclaration(payload);
      if ('error' in parsed) {
        this.options.onLog?.('warn', `ai_actions_declare dropped: ${parsed.error}`);
        return;
      }
      this.applyOrHoldAiDeclaration(parsed.declaration);
      return;
    }

    // Plugin state snapshot for the assistant. Size-capped HARD (the server
    // does not bound archeContext.state and stringifies it into the system
    // prompt every turn — oversized snapshots are dropped with a warn) and
    // frequency-coalesced: each accepted snapshot re-renders the host
    // (setState → merge/filter recompute), so a plugin pushing per-frame
    // must not drive the host at frame rate. Trailing edge — the LATEST
    // snapshot always lands.
    if (data.type === 'ai_state') {
      if (!this.permissions.includes('ai:actions')) {
        this.options.onLog?.('warn', 'ai_state ignored: "ai:actions" permission not granted');
        return;
      }
      const payload = (data as BridgeMessage).payload;
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        this.options.onLog?.('warn', 'ai_state dropped: payload must be an object');
        return;
      }
      let bytes = 0;
      try {
        bytes = JSON.stringify(payload).length;
      } catch {
        this.options.onLog?.('warn', 'ai_state dropped: payload not serializable');
        return;
      }
      if (bytes > AI_STATE_MAX_BYTES) {
        this.options.onLog?.(
          'warn',
          `ai_state dropped: ${bytes} bytes exceeds the ${AI_STATE_MAX_BYTES}-byte cap (the snapshot enters every turn's system prompt)`,
        );
        return;
      }
      // Same banned-character scan as action text: trusted snapshots reach
      // the system prompt verbatim, and control/bidi/zero-width characters
      // have no legitimate use in a state flag map at any trust level.
      // Deep walk, not a serialized scan — JSON.stringify escapes C0 bytes.
      if (containsBannedChars(payload)) {
        this.options.onLog?.('warn', 'ai_state dropped: contains control characters');
        return;
      }
      this.applyOrHoldAiState(payload as Record<string, unknown>);
      return;
    }

    // Plugin requests the HOST open the global Fias AI panel — the
    // discoverability lane's only plugin-initiated affordance. A BARE signal
    // by design: no payload is read, ever — an "open with this prefilled
    // message" variant would be a prompt-injection channel from an untrusted
    // iframe into the assistant's input. Same host-UI-mediation transport
    // exception as the rest of the ai_* lane (docs/capability-placement.md).
    if (data.type === 'ai_open') {
      if (!this.permissions.includes('ai:actions')) {
        this.options.onLog?.('warn', 'ai_open ignored: "ai:actions" permission not granted');
        return;
      }
      const now = Date.now();
      if (now - this.lastAiOpenAt < AI_OPEN_MIN_INTERVAL_MS) {
        this.options.onLog?.(
          'warn',
          `ai_open ignored: debounced (at most one open request per ${AI_OPEN_MIN_INTERVAL_MS / 1000}s)`,
        );
        return;
      }
      this.lastAiOpenAt = now;
      this.options.onAiOpenRequest?.();
      return;
    }

    // Plugin's reply to an ai_action_dispatch — completes the in-flight
    // Promise the renderer's applyAction is awaiting.
    if (data.type === 'ai_action_result') {
      // Uniform lane gate. Redundant today (dispatches only exist for
      // declared actions, which require the permission) but keeps the whole
      // ai_* lane permission-gated without relying on that reasoning.
      if (!this.permissions.includes('ai:actions')) {
        this.options.onLog?.(
          'warn',
          'ai_action_result ignored: "ai:actions" permission not granted',
        );
        return;
      }
      const pending = this.pendingAiDispatches.get((data as BridgeMessage).messageId);
      if (!pending) return; // straggler reply after timeout — already settled
      clearTimeout(pending.timer);
      this.pendingAiDispatches.delete((data as BridgeMessage).messageId);
      const payload = (data as BridgeMessage).payload as
        | { ok?: boolean; error?: string }
        | undefined;
      if (payload?.ok === true) {
        pending.resolve();
      } else {
        pending.reject(
          new Error(
            typeof payload?.error === 'string' &&
              payload.error.length > 0 &&
              payload.error.length <= 1000
              ? payload.error
              : 'the plugin reported the action failed',
          ),
        );
      }
      return;
    }

    if (data.type === 'navigate') {
      const { path, replace } = data.payload as { path: string; replace?: unknown };
      if (typeof path !== 'string' || path.length === 0) {
        const message = `Bridge navigate rejected: path must be a non-empty string (got: ${typeof path === 'string' ? '<empty>' : '<missing>'})`;
        this.options.onLog?.('error', message);
        this.sendError(data.messageId, message);
        return;
      }
      // Optional, additive (protocol v1): absent means push, exactly as before
      // the field existed. Anything but a boolean is refused rather than coerced
      // — `'false'` is truthy, and a history action the plugin did not ask for
      // is worse than an error it can see.
      if (replace !== undefined && typeof replace !== 'boolean') {
        const message = `Bridge navigate rejected: replace must be a boolean when present (got: ${replace === null ? 'null' : typeof replace})`;
        this.options.onLog?.('error', message);
        this.sendError(data.messageId, message);
        return;
      }
      // Plugins navigate in their own URL space (e.g. `/edit/<id>` or
      // `/shared/<id>`); the platform's URL space is `/a/<segment>/...`.
      // Auto-prepend the prefix the plugin is currently mounted under when it
      // sends a root-relative path, so plugin code doesn't have to know its
      // own archeId or the platform path convention. Mirrors how
      // `currentPath` is delivered inbound — both sides speak the plugin's
      // own path space.
      //
      // A path already in absolute `/a/<ours>/...` form is accepted under
      // EITHER spelling of "ours" — the mounted segment (usually the slug) or
      // the canonical archeId — and is then re-expressed against the mounted
      // one. Anything else under `/a/` is rejected so a plugin can't navigate
      // the parent window into someone else's arche space.
      const urlPrefix = this.ownUrlPrefix();
      const canonicalPrefix = `/a/${this.options.archId}`;
      const ownPrefixes =
        urlPrefix === canonicalPrefix ? [canonicalPrefix] : [urlPrefix, canonicalPrefix];

      // '' means the plugin's home; a non-empty value always starts with '/'.
      let subPath: string | null = null;
      for (const prefix of ownPrefixes) {
        if (path === prefix) {
          subPath = '';
          break;
        }
        if (path.startsWith(`${prefix}/`)) {
          subPath = path.slice(prefix.length);
          break;
        }
      }
      if (subPath === null && path.startsWith('/a/')) {
        const message = `Bridge navigate rejected: cross-arche navigation not allowed (got: ${path})`;
        this.options.onLog?.('error', message);
        this.sendError(data.messageId, message);
        return;
      }
      if (subPath === null && path.startsWith('/')) {
        subPath = path === '/' ? '' : path;
      }
      if (subPath === null) {
        const message = `Bridge navigate rejected: path must be root-relative (got: ${path})`;
        this.options.onLog?.('error', message);
        this.sendError(data.messageId, message);
        return;
      }
      const resolved = `${urlPrefix}${subPath}`;
      this.options.onNavigate?.(resolved, { replace: replace === true });
      // Acknowledge so the plugin's `await bridge.request('navigate', ...)`
      // resolves instead of timing out 30s later.
      this.sendResponse(data.messageId, null);
      return;
    }

    // Request/response messages — check permissions and rate limits
    try {
      const result = await this.handleRequest(data);
      this.sendResponse(data.messageId, result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      // Surface the error to the preview log stream so get_preview_logs picks
      // it up. Without this the plugin gets a rejected Promise but the AI
      // has no visibility unless the plugin code explicitly console.errors.
      // BridgeBurstError is the exception: the burst path manages its own
      // onLog (fires once on breach, silent during cooldown) — re-logging
      // here would double-log the breach and drown the logs during cooldown.
      if (!(err instanceof BridgeBurstError)) {
        this.options.onLog?.('error', `Bridge request failed (${data.type}): ${message}`);
      }
      // Forward the backend's machine-readable code (when the failure came
      // from a backend fetch) so the SDK-side FiasBridgeError carries it.
      this.sendError(
        data.messageId,
        message,
        err instanceof HostBridgeError ? err.code : undefined,
        err instanceof HostBridgeError ? err.requestId : undefined,
      );
    }
  }

  private async handleRequest(msg: BridgeMessage): Promise<unknown> {
    // Permission checks run in ALL modes, including preview. The builder/dev
    // preview must fail exactly as a deployed arche would when the manifest
    // omits a permission the code uses — otherwise a missing-permission bug
    // (e.g. calling useFiasUser() without `user:profile:read`) stays hidden
    // until production, where it surfaces as an infinite "loading" hang. The
    // in-memory preview backends below (storage/data/etc.) are unaffected;
    // only the capability gate is now enforced identically to production.
    const requiredPermission = isUserFilesInPlaceSave(msg)
      ? // The composite op's static permission governs the arche's OWN
        // documents. Pointed at the user's files it is a different capability
        // with its own scope — holding one must never stand in for the other.
        VAULT_USER_DOCUMENTS_EDIT_PERMISSION
      : PERMISSION_MAP[msg.type];
    if (requiredPermission && !this.permissions.includes(requiredPermission)) {
      throw new HostBridgeError(
        `Permission denied: ${requiredPermission} not granted`,
        'PERMISSION_DENIED',
      );
    }

    // Rate limiting applies in all modes (protects against runaway loops in preview)
    this.checkRateLimit(msg.type, msg.payload);

    switch (msg.type) {
      case 'get_user':
        return this.options.getUser();

      case 'get_theme':
        return this.options.getTheme();

      case 'open_arche': {
        // Cross-arche navigation. The permission check above
        // (`navigation:open_arche`) and the validation here are the whole trust
        // boundary — the host builds the URL and navigates itself, so the iframe
        // sandbox never needs popups or top-navigation.
        //
        // The plugin never supplies a URL, only an id and (optionally) a
        // sub-path from a fixed slug alphabet, which the host interpolates under
        // `/a/<validated id>/`. So it still cannot name an origin, a scheme, or
        // a route outside the target arche. The sub-path is further restricted
        // to `arc_` targets (see `acceptsSubPath`): a first-party `arche_<slug>`
        // has hand-registered routes under its own prefix, some of them
        // no-auth, and steering the top frame into one of those is a capability
        // a plugin has never had and does not get here.
        const payload =
          msg.payload && typeof msg.payload === 'object'
            ? (msg.payload as {
                archeId?: unknown;
                newTab?: unknown;
                path?: unknown;
                payload?: unknown;
              })
            : null;
        const archeId = typeof payload?.archeId === 'string' ? payload.archeId : '';
        if (!isValidArcheIdFormat(archeId)) {
          throw new Error(
            `open_arche rejected: archeId must be a valid arche id (got: ${archeId ? archeId : '<missing>'})`,
          );
        }
        // Absent is fine; present-and-unusable is an error, never a silent drop
        // — a plugin that asked for a page and landed on the home screen would
        // have no way to tell that from the page simply not existing.
        let path: string | undefined;
        if (payload?.path !== undefined) {
          if (typeof payload.path !== 'string' || !isValidArcheSubPath(payload.path)) {
            throw new Error(
              `open_arche rejected: path must be one or two "[A-Za-z0-9_-]" segments with no leading slash (got: ${
                typeof payload.path === 'string' ? payload.path : '<non-string>'
              })`,
            );
          }
          if (!acceptsSubPath(archeId)) {
            throw new Error(
              `open_arche rejected: a path is only accepted for a plugin arche id (arc_ + 32 hex chars), not ${archeId}`,
            );
          }
          path = payload.path;
        }
        // Optional handoff payload — the plugin says WHAT it is handing over;
        // the host decides whether the target will take it. Validated against
        // the same contract the receiving intake re-validates against, so a
        // payload that would be dropped on arrival is refused here instead,
        // where the plugin can see why.
        let handoff: ArcheHandoffPayload | undefined;
        if (payload?.payload !== undefined) {
          const parsed = parseArcheHandoffPayload(payload.payload);
          if (!parsed) {
            throw new Error(
              'open_arche rejected: payload is not a valid handoff payload (check the kind and its required fields)',
            );
          }
          // A REPLY answers a request the platform recorded; it is not a
          // plugin's to mint. Refused BEFORE the targeting check, whose
          // reply-kind leg deliberately accepts any arche — the same carve-out
          // that makes a forged reply routable is what makes this gate the
          // only thing standing in front of it. (Harmless today: a consumer
          // matches a result to an OUTSTANDING requestId it generated itself,
          // so a forged one matches nothing. Closing the category is cheaper
          // than relying on every future consumer to keep that discipline.)
          if (isHandoffReplyKind(parsed.kind)) {
            throw new Error(
              `open_arche rejected: '${parsed.kind}' is a reply, produced by the platform — not something an arche sends`,
            );
          }
          if (!handoffTargetsArche(parsed, archeId)) {
            throw new Error(
              `open_arche rejected: a '${parsed.kind}' payload is not accepted by ${archeId}`,
            );
          }
          handoff = parsed;
        }
        this.options.onOpenArche?.(archeId, {
          newTab: payload?.newTab === true,
          path,
          payload: handoff,
          // Host-stamped, never plugin-supplied. See the option's docstring.
          originArcheId: this.options.archId,
        });
        return null;
      }

      case 'entity_invoke': {
        const payload = msg.payload as {
          // String id, or a selector object ({ capability } for text) — both
          // are valid SDK forms; the resolvers downstream handle each.
          entityId: string | Record<string, unknown>;
          input: string;
          stream?: boolean;
          systemPrompt?: string;
          images?: Array<{ mediaType: string; dataBase64: string }>;
          // Forwarded verbatim (the whole msg is proxied); the server clamps
          // temperature/maxTokens and validates `reasoning`. Declared so the
          // shape is visible here rather than implied.
          parameters?: Record<string, unknown>;
          // Prior conversation turns; forwarded verbatim, validated server-side.
          history?: unknown;
        };
        // Path-dependent permission gate (ADR-012 Phase M4) — the op has no
        // PERMISSION_MAP entry; the required permission depends on the
        // target entity. Runs in ALL modes (preview parity: a missing
        // manifest permission must fail here exactly as in production).
        const requiredEntityInvokePermission = entityInvokeRequiredPermission(payload.entityId);
        if (!this.permissions.includes(requiredEntityInvokePermission)) {
          throw new Error(`Permission denied: ${requiredEntityInvokePermission} not granted`);
        }
        // Store (IAP) via entity_invoke (ADR-012 Phase L, Job 3): route to the
        // shared store handler so the host-mediated purchase-confirmation +
        // preview simulation + the store family's legacy wire SHAPES are
        // preserved on the entity_invoke path exactly as on the `store_*`
        // aliases. Store is NOT a synthetic-tenant preview entity, so preview
        // MUST short-circuit client-side (inside handleStoreOp) and never proxy.
        if (payload.entityId === PLUGIN_STORE_ENTITY_ID) {
          const inner = ((msg.payload as { payload?: Record<string, unknown> }).payload ??
            {}) as Record<string, unknown>;
          const { op: rawOp, ...storeArgs } = inner;
          const storeOp = typeof rawOp === 'string' ? rawOp : '';
          return this.handleStoreOp(storeOp, storeArgs, async (o, a) => {
            const body = await this.proxyToBackend({
              ...msg,
              payload: { entityId: PLUGIN_STORE_ENTITY_ID, payload: { op: o, ...a } },
            });
            return (body as { output?: unknown }).output;
          });
        }
        // Mirrored integration entities (the free/state families) go to the
        // live bridge EVEN IN PREVIEW — M3 serves preview tenants on the
        // production pipeline; only AI/text invocation stays on the preview
        // session hooks below.
        const isMirroredIntegration =
          typeof payload.entityId === 'string' &&
          INTEGRATION_ENTITY_PERMISSIONS[payload.entityId] !== undefined;
        if (this.options.previewMode && this.options.onEntityInvoke && !isMirroredIntegration) {
          if (payload.stream) {
            return this.handlePreviewStreamingInvoke(msg.messageId, payload);
          }
          return this.options.onEntityInvoke(payload);
        }
        // Published plugins. BOTH transports carry the host's acting workspace
        // (`withActingHolder`): the streaming path forwards the payload as its
        // own request body, so stamping only the proxied path would leave a
        // `stream: true` call free to name a workspace of the iframe's choosing.
        const outbound = this.withActingHolder(msg);
        if (payload.stream) {
          return this.handlePublishedStreamingInvoke(
            msg.messageId,
            outbound.payload as Parameters<typeof this.handlePublishedStreamingInvoke>[1],
          );
        }
        const body = await this.proxyToBackend(outbound);
        // Subscribe-kind verb responses carry a subscription envelope — the
        // manager consumes grant/topic and hands the iframe a host-minted
        // logical id. Everything else passes through untouched. The payload
        // it keeps for renewal/reconnect is the HOST-STAMPED one: replay
        // bypasses withActingHolder, so storing the iframe's raw payload
        // would let a renewal carry whatever acting workspace the iframe named.
        return this.getSubscriptionManager().handleInvokeResponse(
          body,
          outbound.payload as Record<string, unknown>,
        );
      }

      case 'image_generate': {
        const imgPayload = msg.payload as {
          entityId: string | Record<string, unknown>;
          prompt: string;
          size?: string;
          quality?: string;
          style?: string;
        };
        if (this.options.previewMode && this.options.onImageGenerate) {
          return this.options.onImageGenerate(imgPayload);
        }
        // Async-via-WebSocket path: POST returns `{ requestId, status:
        // 'pending' }` immediately; the final `ImageInvokeResult`
        // arrives over WS as an `image_complete` broadcast. Pre-refactor
        // this case awaited the full 30–90 s provider call on a single
        // HTTP request and hit the internal ALB's 60 s idle timeout. See
        // image-generate-bridge-handler.ts file header.
        return this.handleAsyncImageGenerate(msg);
      }

      case 'entities_list_image': {
        // Discovery op backing the SDK's `useImageEntities` hook. Pure
        // registry read on the server — no preview-mode hook needed
        // (preview can call the live registry and stays in sync with
        // production).
        return this.proxyToBackend(msg);
      }

      case 'client_entity_invoke': {
        // Generic dispatcher for `executionMode: 'client-side'` entities.
        // Resolves the entity registration by ID (from the client-side
        // registry — populated by executor module imports at startup),
        // gates on the entity's declared `pluginPermission`, and hands off
        // to the registry. No per-entity case needed: adding a new
        // client-side entity is an entity-config + executor change, never
        // a bridge change.
        const payload =
          msg.payload && typeof msg.payload === 'object'
            ? (msg.payload as { entityId?: unknown; input?: unknown })
            : null;
        if (!payload || typeof payload.entityId !== 'string') {
          throw new Error('client_entity_invoke: payload.entityId (string) is required');
        }
        const registration = getClientEntityRegistration(payload.entityId);
        if (!registration) {
          throw new Error(
            `client_entity_invoke: no client-side executor registered for ${payload.entityId}`,
          );
        }
        // Per-entity permission (resolved from the entity's registration).
        // Enforced in all modes, including preview — parity with production
        // and with the centralized check in handleRequest above.
        const requiredPermission = registration.pluginPermission as PluginPermission;
        if (!this.permissions.includes(requiredPermission)) {
          throw new Error(`Permission denied: ${requiredPermission} not granted`);
        }
        const user = await this.options.getUser();
        return invokeClientEntity(payload.entityId, payload.input, {
          userId: user.userId,
          archeId: this.options.archId,
        });
      }

      case 'audio_generate': {
        // Audio generation runs through the server-side `audio_generate`
        // bridge op. In builder preview the server answers 403
        // PREVIEW_UNSUPPORTED_OP (billed path with no builder-session cost
        // accounting — see preview-bridge-gate.ts); the rejection surfaces
        // in the preview logs with that message.
        return this.proxyToBackend(msg);
      }

      // Storage / Data Store / Workspaces / Store-IAP: the legacy op-family
      // alias ops were retired in ADR-012 Phase L Job 3 Step 2. New bundles
      // reach these capabilities via `entity_invoke` (handled in the case
      // above — the mirrored-integration branch proxies to the live bridge in
      // preview and production alike; store routes through the shared
      // `handleStoreOp`). An old, immutable published bundle that still emits a
      // bare alias falls through to `default`, which raises a clear "plugin
      // needs an update from its developer" notice rather than a cryptic error.

      // ── Vault Documents (Phase 12 substrate, scoped to source_arche_id) ──
      // The server-side bridge handler enforces source_arche_id == this arche
      // (or attachment via vault_document_references) and proxies through
      // vault-documents-operations.ts. Preview mode has no Vault store, so
      // these return empty results / synthetic values to keep render paths
      // from crashing.
      // ── Vault user-document picker (host-UI mediation) ──────────────
      // The purchase-sheet pattern applied to user data: the HOST renders
      // the picker + consent sheet; the plugin never sees the user's vault
      // list, only what was hand-picked and confirmed (metadata — content
      // flows through the audited consented-read entity verbs). Client-
      // host-only op (no server case), like `open_arche`; sanctioned as a
      // transport exception in docs/capability-placement.md + ADR-012.
      case 'vault_documents_pick': {
        if (this.options.previewMode || !this.options.onVaultPickRequest) {
          // No user data in the preview, ever — and a host without the
          // callback (embeds, tests) fails safe as a cancel.
          return { canceled: true };
        }
        const req = msg.payload as {
          maxDocuments?: unknown;
          access?: unknown;
          documentId?: unknown;
          accept?: unknown;
        } | null;
        const maxDocuments = Math.max(
          1,
          Math.min(typeof req?.maxDocuments === 'number' ? req.maxDocuments : 25, 25),
        );
        // Asking for EDIT access needs its own permission. Checked here so the
        // user is never shown an "allow editing?" sheet for an app the server
        // would then refuse (`ARCHE_CANNOT_EDIT`) — the server check is the
        // real one; this only keeps the prompt honest.
        if (req?.access !== undefined && req.access !== 'read' && req.access !== 'write') {
          throw new HostBridgeError(
            'vault_documents_pick: access must be "read" or "write"',
            'INVALID_PARAMS',
          );
        }
        const access = req?.access === 'write' ? 'write' : 'read';
        if (access === 'write' && !this.permissions.includes('vault:user-documents:edit')) {
          throw new HostBridgeError(
            "Editing the user's files requires the vault:user-documents:edit permission.",
            'PERMISSION_DENIED',
          );
        }
        const documentId =
          typeof req?.documentId === 'string' && /^vdoc_[a-f0-9]{32}$/.test(req.documentId)
            ? req.documentId
            : undefined;
        let accept: string[] | undefined;
        if (req?.accept !== undefined) {
          if (
            !Array.isArray(req.accept) ||
            req.accept.length > MAX_ACCEPT_ENTRIES ||
            req.accept.some((t) => typeof t !== 'string' || !ACCEPT_ENTRY_RE.test(t))
          ) {
            throw new HostBridgeError(
              `vault_documents_pick: accept must be up to ${MAX_ACCEPT_ENTRIES} MIME types, ` +
                "'type/*' families or '.ext' extensions",
              'INVALID_PARAMS',
            );
          }
          accept = req.accept as string[];
        }
        return this.options.onVaultPickRequest({
          maxDocuments,
          access,
          documentId,
          ...(accept ? { accept } : {}),
        });
      }

      // Vault ops in preview FAIL FAST, mirroring the server's
      // PREVIEW_UNSUPPORTED_OP. These used to be success-shaped stubs
      // (write returned a synthetic documentId, update/delete returned
      // success, list/read returned empty) — so in the builder preview a
      // "successful" write silently vanished, the exact
      // preview-lies-about-production failure class the capability work
      // exists to kill (first surfaced by the kitchen-sink verification
      // arche). The documented behavior — catalog previewNotes, the
      // derived preview section, SDK CLAUDE.md — has always been "vault
      // ops are not available in preview"; now the code says the same.
      case 'vault_documents_list':
      case 'vault_documents_read':
      case 'vault_documents_read_many':
      case 'vault_documents_download_url':
      case 'vault_documents_get_urls':
      case 'vault_documents_write':
      case 'vault_documents_update':
      case 'vault_documents_delete':
      case 'vault_documents_search':
      case 'vault_documents_attach':
      case 'vault_documents_detach': {
        if (this.options.previewMode) {
          throw new Error(
            `${msg.type} is not available in the builder preview — vault documents first run for real after publish (or in the dev harness's Live mode).`,
          );
        }
        // `saveCopy`: the arche hands the user a COPY of one of its own
        // files, through the host Save dialog. Host-only — never forwarded.
        if (
          msg.type === 'vault_documents_update' &&
          msg.payload !== null &&
          typeof msg.payload === 'object' &&
          'saveCopy' in msg.payload
        ) {
          return this.handleSaveCopy(msg.payload);
        }
        // `userVisible` on an update used to PROMOTE the document into the
        // user's files — a visibility change disguised as an update. That is
        // gone: an arche hands the user a finished file by saving a copy
        // through the host Save dialog. The key is still intercepted, never
        // forwarded, so an older plugin gets a clear answer rather than the
        // server's generic strict-schema refusal.
        if (
          msg.type === 'vault_documents_update' &&
          msg.payload !== null &&
          typeof msg.payload === 'object' &&
          'userVisible' in msg.payload
        ) {
          throw new HostBridgeError(
            "Moving a file into the user's files is no longer supported. Save a copy into their Documents instead.",
            'PROMOTE_REMOVED',
          );
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_upload_init':
      case 'vault_documents_upload_finalize': {
        if (this.options.previewMode) {
          // The two-step upload protocol (init → PUT bytes to
          // presigned URL → finalize) needs a real PUT endpoint that
          // preview mode cannot provide. Returning a synthetic
          // uploadUrl like 'about:blank' would just produce a
          // confusing fetch failure when the SDK tries to upload.
          // Fail fast so plugin code can show a clearer message.
          throw new Error(
            `${msg.type} is not supported in preview mode (no presigned-URL endpoint). Test binary uploads in the published or dev-harness-live runtime.`,
          );
        }
        // `visibility` is HOST-ONLY on this op. The composite
        // `vault_documents_upload` sets it after the user confirms the save in
        // host chrome, and it reaches the backend through `callBackendOp` —
        // never through this relay. Letting the iframe pass it here would hand
        // a plugin holding `vault:user-documents:write` an unattended write
        // into the user's own files: the whole point of the confirmation is
        // that no such standing capability exists.
        //
        // Stripping silently would be worse than refusing — a plugin would
        // believe it saved to the user's files and report success for a
        // document that landed arche-private.
        if (
          msg.type === 'vault_documents_upload_init' &&
          (msg.payload as { visibility?: unknown } | null)?.visibility !== undefined
        ) {
          throw new HostBridgeError(
            "vault_documents_upload_init does not accept `visibility`. To save into the user's " +
              "files, call upload({ destination: 'documents' }) so the host can show its Save " +
              'dialog.',
            'INVALID_PARAMS',
          );
        }
        return this.proxyToBackend(msg);
      }

      case 'vault_documents_upload': {
        // Composite binary upload, host-orchestrated. The plugin iframe's
        // CSP is `connect-src 'none'`, so it CANNOT do the presigned PUT
        // itself (the SDK's old in-iframe `fetch` PUT was silently blocked
        // in published plugins). Here the HOST (parent window, no
        // connect-src restriction) runs init → PUT → finalize. The presigned
        // URL comes from the BACKEND (init), never from the iframe, so the
        // iframe cannot point the host at an arbitrary URL (no SSRF).
        if (this.options.previewMode) {
          throw new Error(
            'vault_documents_upload is not supported in preview mode (no presigned-URL endpoint). Test binary uploads in the published or dev-harness-live runtime.',
          );
        }
        return this.handleVaultUpload(msg.payload);
      }

      case 'surface_invoke': {
        // Invocation Surface Pattern dispatch. Per-surface permission,
        // rate-limit, and validation gates all read from the surface
        // metadata server-side, so there's no entry in PERMISSION_MAP
        // here (matches the production backend's gating model).
        //
        // Preview mode proxies to the live bridge like every other op: the
        // server admits surfaces that declare `previewPolicy` and answers 403
        // PREVIEW_UNSUPPORTED_OP for the rest, so the eligibility decision
        // stays in one place instead of being duplicated as a client throw
        // that could drift from it. Preview calls run for real and are billed
        // to the author.
        // Async-capable: surfaces with `asyncDispatch` return `{ requestId,
        // status: 'pending' }` and complete over WebSocket (see
        // surface-async.ts). Synchronous surfaces return their result on the
        // HTTP response and this method passes it straight through.
        return this.handleAsyncSurfaceInvoke(msg);
      }

      // ── Arche Asset Library (contributor-curated published manifest) ──
      // The bridge backend resolves the manifest from the URL path's
      // archeId (server-trusted) and signs short-lived URLs against
      // the published entries. Preview mode has no manifest, so we
      // return an empty list / a synthetic URL.
      case 'arche_assets_list': {
        if (this.options.previewMode) {
          return { entries: [], nextCursor: null };
        }
        return this.proxyToBackend(msg);
      }
      case 'arche_assets_index': {
        if (this.options.previewMode) {
          return { groups: [] };
        }
        return this.proxyToBackend(msg);
      }
      case 'arche_assets_get_url': {
        if (this.options.previewMode) {
          return {
            assetId: 'preview',
            signedUrl: 'about:blank',
            expiresAt: Math.floor(Date.now() / 1000) + 60,
            contentType: 'image/png',
            width: 0,
            height: 0,
            sizeBytes: 0,
            name: null,
            altText: null,
            tags: [],
          };
        }
        return this.proxyToBackend(msg);
      }

      default:
        // ADR-012 Phase L Job 3 Step 2: the legacy op-family aliases
        // (storage_*/data_*/workspace_*/store_*) were retired. An old, immutable
        // published bundle still emits them; surface a clear "plugin needs an
        // update from its developer" notice instead of a cryptic unknown-op error.
        if (RETIRED_ALIAS_OP_RE.test(msg.type)) {
          this.options.onRetiredCapability?.(msg.type);
          throw new Error(
            `This plugin uses a retired capability (${msg.type}) and needs an ` +
              `update from its developer.`,
          );
        }
        throw new Error(`Unknown bridge call type: ${msg.type}`);
    }
  }

  /**
   * Handle streaming entity invoke in preview mode via SSE.
   * Sends stream_token messages to the plugin as tokens arrive,
   * then returns the final result for the normal response message.
   */
  private async handlePreviewStreamingInvoke(
    messageId: string,
    payload: {
      // String config id, or a selector object ({ capability } et al) — the
      // ai-invoke route resolves both (published-bridge parity; the preview
      // used to 400 every documented selector form — kitchen-sink finding).
      entityId: string | Record<string, unknown>;
      input: string;
      systemPrompt?: string;
      images?: Array<{ mediaType: string; dataBase64: string }>;
      /** Only `reasoning` is read by the preview route; validated there. */
      parameters?: Record<string, unknown>;
      /** Prior conversation turns; validated server-side. */
      history?: unknown;
    },
  ): Promise<unknown> {
    // Preview AI invoke authenticates as the user's SESSION (cookie +
    // CSRF), exactly like the non-streaming preview path
    // (api.invokePluginAI). The `/ai-invoke` route is session-auth
    // (requireAuth); a plugin-bridge capability token is the WRONG
    // credential here. Sending one — or the empty string the builder's
    // own arche (`arche_plugin_builder`, renderMode 'default') yields
    // when its bridge-token mint 400s — produced the "JWT … 3 parts"
    // 401 that surfaced to users as "AI not working". Regressed when the
    // bridge moved to capability tokens (commit 0a08f3c3); `getAuthToken`
    // stays correct for the published bridge path, just not here.
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    attachCsrfHeader('POST', headers);
    const response = await fetch(
      `${this.options.bridgeApiUrl}/arches/plugin-builder/sessions/${this.options.archId.replace('preview_', '')}/ai-invoke`,
      {
        method: 'POST',
        headers,
        credentials: sessionCredentials(),
        body: JSON.stringify({
          input: payload.input,
          entityId: payload.entityId,
          stream: true,
          // Invoke-time systemPrompt — the external-SDK norm; the route gives
          // it precedence over a builder config's stored prompt, matching the
          // published bridge. Previously dropped here, so selector-form
          // invokes silently lost their instructions.
          ...(payload.systemPrompt ? { systemPrompt: payload.systemPrompt } : {}),
          // Vision inputs ride along — the ai-invoke route validates them with
          // the same bounds as the published bridge. Omitted when absent so
          // text-only calls keep their exact prior body shape.
          ...(payload.images && payload.images.length > 0 ? { images: payload.images } : {}),
          // Multi-turn history and per-call parameters (the route reads
          // `parameters.reasoning`); validated server-side with the published
          // bridge's own schemas. Omitted when absent — same prior body shape.
          ...(payload.history !== undefined ? { history: payload.history } : {}),
          ...(payload.parameters !== undefined ? { parameters: payload.parameters } : {}),
        }),
      },
    );

    if (!response.ok) {
      if (response.status === 413) throw requestTooLargeError();
      const err = await response.json().catch(() => ({ error: 'AI invocation failed' }));
      throw new HostBridgeError(
        err.error?.message ?? 'AI invocation failed',
        err.error?.code ?? 'BRIDGE_ERROR',
        errorRequestIdOf(err),
      );
    }

    if (response.headers.get('content-type')?.includes('text/event-stream') && response.body) {
      return this.relayEntityInvokeStream(messageId, response.body);
    }

    // Non-streaming fallback
    return response.json();
  }

  /**
   * Relay a streaming `entity_invoke` SSE body to the plugin iframe and return
   * the final result. Shared by the preview and published paths so the two
   * can never frame the stream differently.
   *
   * Frames (server → host → plugin `stream_token`):
   * - `{ text }` → `stream_token` (answer text)
   * - `{ thinking }` → `stream_token` with `channel: 'thinking'`
   * - `: keepalive` comment → `stream_token` with `channel: 'keepalive'`, so
   *   the SDK's idle timeout sees a live stream during silent reasoning
   * - `{ done, output, metadata }` → the resolved result (usage included)
   * - `{ error }` → thrown as a HostBridgeError
   */
  private async relayEntityInvokeStream(
    messageId: string,
    body: ReadableStream<Uint8Array>,
  ): Promise<{ output: string; metadata?: Record<string, unknown> }> {
    const postToken = (message: { text?: string; channel?: 'thinking' | 'keepalive' }) =>
      this.options.iframe.contentWindow?.postMessage(
        { type: 'stream_token', messageId, ...message },
        '*',
      );
    let fullOutput = '';
    let metadata: Record<string, unknown> | undefined;
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (line.startsWith(':')) {
          postToken({ channel: 'keepalive' });
          continue;
        }
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6);
        if (data === '[DONE]') continue;
        let parsed: {
          text?: string;
          thinking?: string;
          done?: boolean;
          output?: string;
          metadata?: Record<string, unknown>;
          error?: unknown;
        } | null = null;
        try {
          parsed = JSON.parse(data);
        } catch {
          // Skip malformed SSE data
          continue;
        }
        // A mid-stream failure frame. Without this branch the reader fell
        // through, resolved with whatever partial text had accumulated, and
        // the plugin saw a SUCCESSFUL call returning a truncated (usually
        // empty) string — a failure mode strictly worse than a clear error.
        if (parsed?.error) {
          // Release the body before unwinding. `retryable: true` encourages
          // plugins to loop on exactly these failures, so a reader left
          // locked until GC accumulates open connections.
          await reader.cancel().catch(() => {});
          throw hostBridgeErrorFromSseFrame(parsed.error);
        }
        if (parsed?.text) {
          fullOutput += parsed.text;
          postToken({ text: parsed.text });
        }
        if (parsed?.thinking) {
          postToken({ text: parsed.thinking, channel: 'thinking' });
        }
        if (parsed?.done) {
          if (parsed.output) fullOutput = parsed.output;
          metadata = parsed.metadata;
        }
      }
    }

    return metadata ? { output: fullOutput, metadata } : { output: fullOutput };
  }

  /**
   * Handle streaming entity invoke for published plugins via the bridge endpoint.
   */
  private async handlePublishedStreamingInvoke(
    messageId: string,
    payload: {
      // String id or selector object — the published bridge already resolves
      // both; JSON-serialized verbatim into the request body.
      entityId: string | Record<string, unknown>;
      input: string;
      systemPrompt?: string;
      images?: Array<{ mediaType: string; dataBase64: string }>;
      /** Spread into the request body below; the server clamps temperature /
       *  maxTokens and validates `reasoning` strictly. */
      parameters?: Record<string, unknown>;
      /** Prior conversation turns; validated server-side. */
      history?: unknown;
    },
  ): Promise<unknown> {
    const token = await this.options.getAuthToken();
    const response = await fetch(this.bridgeEndpoint('entity_invoke', payload), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        type: 'entity_invoke',
        payload: { ...payload, stream: true },
      }),
    });

    if (!response.ok) {
      if (response.status === 413) throw requestTooLargeError();
      const err = await response.json().catch(() => ({ error: 'AI invocation failed' }));
      throw new HostBridgeError(
        err.error?.message ?? 'AI invocation failed',
        err.error?.code ?? 'BRIDGE_ERROR',
        errorRequestIdOf(err),
      );
    }

    if (response.headers.get('content-type')?.includes('text/event-stream') && response.body) {
      return this.relayEntityInvokeStream(messageId, response.body);
    }

    // Non-SSE fallback
    return response.json();
  }

  /**
   * Escalating poll fallback shared by the image and surface async paths.
   * Stays quiet for `ASYNC_POLL_GRACE_MS` (the WS path normally wins first),
   * then polls every `ASYNC_POLL_INTERVAL_MS` until a terminal result or the
   * `ASYNC_POLL_MAX_MS` ceiling. Returns a canceller the caller wires into its
   * `cleanup()` so a WS-delivered result (or host teardown) stops polling.
   *
   * `poll` is the per-surface single-shot status fetch (already auth-scoped on
   * the server). `onSettled` is invoked at most once with the outcome; the
   * loop guarantees no callback after the canceller runs.
   */
  private startPollFallback(
    poll: () => Promise<
      | { kind: 'pending' }
      | { kind: 'completed'; value: unknown }
      | { kind: 'failed'; code: string; message: string }
      | { kind: 'not_found' }
    >,
    onSettled: (
      outcome:
        | { type: 'completed'; value: unknown }
        | { type: 'failed'; error: Error }
        | { type: 'timeout' },
    ) => void,
    maxMs: number = ASYNC_POLL_MAX_MS,
  ): () => void {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = Date.now() + maxMs;

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      let result: Awaited<ReturnType<typeof poll>>;
      try {
        result = await poll();
      } catch {
        result = { kind: 'pending' };
      }
      if (cancelled) return;

      if (result.kind === 'completed') {
        onSettled({ type: 'completed', value: result.value });
        return;
      }
      if (result.kind === 'failed') {
        onSettled({ type: 'failed', error: new Error(`${result.code}: ${result.message}`) });
        return;
      }
      // `not_found` means the server has no record of this requestId on any
      // replica (the durable request_state row makes this authoritative) —
      // stop rather than poll out the ceiling.
      if (result.kind === 'not_found' || Date.now() >= deadline) {
        onSettled({ type: 'timeout' });
        return;
      }
      timer = setTimeout(() => void tick(), ASYNC_POLL_INTERVAL_MS);
    };

    timer = setTimeout(() => void tick(), ASYNC_POLL_GRACE_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }

  /**
   * Async-via-WebSocket dispatch for `image_generate`.
   *
   * Steps:
   *   1. POST the kickoff. The server returns `{ requestId, status:
   *      'pending' }` in ~10 ms.
   *   2. Subscribe to the shared `FiasWebSocket` for that requestId.
   *      `getWebSocket` lazily fetches its own Cognito-gated ticket
   *      via `authApi.getWebSocketTicket()`, so no extra auth wiring.
   *   3. Register `onImageComplete` + `onError` callbacks filtered by
   *      requestId. Broadcasts for any other in-flight request the
   *      user owns no-op.
   *   4. On match: resolve with the final `ImageInvokeResult` payload
   *      shape (strip broadcast-only `type` and `requestId` fields).
   *      The outer `handleMessage` in turn posts this into the iframe
   *      so the SDK's `await fias.images.generate(...)` resolves
   *      byte-for-byte the same as the pre-refactor synchronous path.
   *   5. Poll fallback: an escalating `image-status` poll (quiet for the
   *      first ~15 s, then every ~5 s up to a 180 s ceiling) recovers a
   *      missed broadcast in seconds. `completed` → resolve; `failed` →
   *      reject; exhausted / `not_found` → reject with
   *      `image_generate_timeout`.
   *   6. Cleanup runs on EVERY terminal path (success / error /
   *      timeout / host destroy) — see `pendingImageRequests`
   *      comment + the cleanup() function below.
   *
   * If the server responds with anything other than `status: 'pending'`
   * (legacy code paths, future inline responses), this method falls
   * through to returning the body as-is.
   */
  private async handleAsyncImageGenerate(msg: BridgeMessage): Promise<unknown> {
    const initialResponse = (await this.proxyToBackend(msg)) as {
      requestId?: string;
      status?: string;
      [key: string]: unknown;
    };
    if (initialResponse?.status !== 'pending' || typeof initialResponse.requestId !== 'string') {
      return initialResponse;
    }
    const requestId = initialResponse.requestId;
    const ws = getWebSocket(WEBSOCKET_ENDPOINT);

    return new Promise<unknown>((resolve, reject) => {
      let settled = false;
      const finish = (cb: () => void): void => {
        if (settled) return;
        settled = true;
        const entry = this.pendingImageRequests.get(requestId);
        if (entry) {
          entry.cleanup();
          this.pendingImageRequests.delete(requestId);
        }
        cb();
      };

      const unsubComplete = ws.onImageComplete((data) => {
        if (data.requestId !== requestId) return;
        // Reconstruct the pre-refactor `ImageInvokeResult` shape
        // byte-for-byte. The broadcast carries broadcast-only metadata
        // (`type`, `requestId`) that the SDK contract did not include
        // — stripping them keeps the iframe-bound payload identical to
        // what the synchronous HTTP response returned pre-refactor.
        const payload: Record<string, unknown> = {
          imageUrl: data.imageUrl,
          imageRef: data.imageRef,
          fileId: data.fileId,
          revisedPrompt: data.revisedPrompt,
          provider: data.provider,
          model: data.model,
          costCents: data.costCents,
        };
        if (data.deprecation) payload.deprecation = data.deprecation;
        finish(() => resolve(payload));
      });

      const unsubError = ws.onError((code, message, errRequestId) => {
        // Only fail THIS entry. Generic connection errors (no requestId
        // — e.g. CONNECTION_FAILED after max reconnect attempts) reach
        // every listener; per-request errors broadcast by the server
        // (e.g. image_generate failures) carry the originating
        // requestId via the extended `ErrorCallback` signature. Both
        // cases land here; the filter below routes them correctly:
        //   - requestId === ours → reject this pending image entry
        //   - requestId undefined → treat as a connection-level error
        //     and reject every pending entry (callers can retry)
        //   - requestId for a different request → ignore
        if (errRequestId !== undefined && errRequestId !== requestId) return;
        finish(() => reject(new Error(`${code}: ${message}`)));
      });

      // Escalating poll fallback. The WS `image_complete` (live or replayed
      // on subscribe) normally settles first and cancels this; if the
      // broadcast was missed, the poll recovers the durable result in seconds.
      const cancelPoll = this.startPollFallback(
        () =>
          this.pollImageStatus(requestId).then((s) =>
            s.kind === 'completed' ? { kind: 'completed' as const, value: s.payload } : s,
          ),
        (outcome) => {
          if (outcome.type === 'completed') {
            finish(() => resolve(outcome.value));
          } else if (outcome.type === 'failed') {
            finish(() => reject(outcome.error));
          } else {
            finish(() => reject(new Error('image_generate_timeout')));
          }
        },
        IMAGE_POLL_MAX_MS,
      );

      const cleanup = (): void => {
        unsubComplete();
        unsubError();
        cancelPoll();
        // Unsubscribe THIS request only. Parameterless `unsubscribe()`
        // would clear every subscription on the shared singleton —
        // including a sibling internal-arche generation in another tab
        // and any other pending plugin image_generate. The targeted
        // form leaves those untouched.
        ws.unsubscribe(requestId);
      };

      this.pendingImageRequests.set(requestId, { resolve, reject, cleanup });

      // Best-effort WS subscribe for the fast path, AFTER the poll fallback is
      // already armed. Detached on purpose: a slow or wedged connect (e.g. the
      // socket is mid-reconnect after a page reload) must NEVER block the poll
      // — that's the durable safety net. See subscribeForAsyncResult.
      void this.subscribeForAsyncResult(ws, requestId);
    });
  }

  /**
   * Best-effort WebSocket subscribe for an async (`image_generate` /
   * `surface_invoke`) result. Runs DETACHED from the request promise so the
   * escalating poll fallback is never gated behind the WebSocket: if the
   * socket is mid-reconnect, the connect hangs, or the ticket fetch fails,
   * the poll still recovers the result from the server's durable
   * `request_state`. On success this is the fast path — the live
   * `image_complete` / `surface_complete` broadcast, or the replay the WS
   * service sends when this (possibly late) subscribe lands after completion,
   * resolves the pending request before the poll's grace window elapses.
   */
  private async subscribeForAsyncResult(
    ws: ReturnType<typeof getWebSocket>,
    requestId: string,
  ): Promise<void> {
    try {
      if (ws.getState() !== 'connected') {
        await ws.connect(() => authApi.getWebSocketTicket());
      }
      // The connect above can await a slow / mid-reconnect socket. If the
      // request already settled (success / error / timeout) or the host was
      // destroyed while we waited, its cleanup() has already run
      // ws.unsubscribe(requestId) — re-subscribing now would orphan a
      // subscription on the process-wide singleton that nothing ever removes
      // (and that re-sends on every reconnect). Only subscribe if the request
      // is still pending. requestIds are unique across both maps.
      if (
        !this.pendingImageRequests.has(requestId) &&
        !this.pendingSurfaceRequests.has(requestId)
      ) {
        return;
      }
      ws.subscribe(requestId);
    } catch {
      // Non-fatal: the poll fallback owns delivery when the WS can't.
    }
  }

  /**
   * Polls `GET /v1/plugins/image-status/:requestId` once. Returns a
   * discriminated union mirroring the server-side 4-state contract.
   * Called only from the 180 s safety-timer path; the happy path
   * resolves via WebSocket.
   */
  private async pollImageStatus(
    requestId: string,
  ): Promise<
    | { kind: 'pending' }
    | { kind: 'completed'; payload: unknown }
    | { kind: 'failed'; code: string; message: string }
    | { kind: 'not_found' }
  > {
    try {
      // Authenticate with the HOST's browser SESSION (the `fias_session`
      // cookie), NOT `getAuthToken()`. `getAuthToken` returns the plugin
      // BRIDGE capability token (for `/bridge/:archId`), but `/plugins/
      // image-status` is `requireAuth` (session) — sending the bridge token
      // there 401s, which silently degrades every poll to `pending` and is
      // why the fallback never recovered a missed broadcast. `credentials:
      // 'include'` matches how the rest of the web app calls session routes.
      const resp = await fetch(
        `${this.options.bridgeApiUrl}/plugins/image-status/${encodeURIComponent(requestId)}`,
        { credentials: sessionCredentials() },
      );
      if (resp.status === 404) return { kind: 'not_found' };
      const body = (await resp.json()) as {
        status?: string;
        payload?: unknown;
        error?: { code?: string; message?: string };
      };
      if (body.status === 'completed') return { kind: 'completed', payload: body.payload };
      if (body.status === 'failed') {
        return {
          kind: 'failed',
          code: body.error?.code ?? 'GENERATION_FAILED',
          message: body.error?.message ?? 'Image generation failed',
        };
      }
      return { kind: 'pending' };
    } catch {
      return { kind: 'pending' };
    }
  }

  /**
   * Async-via-WebSocket dispatch for `surface_invoke`. Generalizes
   * `handleAsyncImageGenerate` to any surface declaring `asyncDispatch`.
   *
   * Steps mirror the image path:
   *   1. POST the kickoff. An async surface returns `{ requestId, status:
   *      'pending' }`; a synchronous surface returns its full result —
   *      detected here and passed straight through (no WS wait).
   *   2. Subscribe to the shared FiasWebSocket for that requestId.
   *   3. Resolve on the `surface_complete` broadcast for THIS requestId
   *      with the opaque `result` (each surface's own `resultSchema`).
   *   4. 180s safety timer → single `surface-status` poll fallback.
   *   5. Cleanup on every terminal path (success/error/timeout/destroy).
   */
  private async handleAsyncSurfaceInvoke(msg: BridgeMessage): Promise<unknown> {
    const initialResponse = (await this.proxyToBackend(msg)) as {
      requestId?: string;
      status?: string;
      [key: string]: unknown;
    };
    if (initialResponse?.status !== 'pending' || typeof initialResponse.requestId !== 'string') {
      return initialResponse;
    }
    const requestId = initialResponse.requestId;
    const ws = getWebSocket(WEBSOCKET_ENDPOINT);

    return new Promise<unknown>((resolve, reject) => {
      let settled = false;
      const finish = (cb: () => void): void => {
        if (settled) return;
        settled = true;
        const entry = this.pendingSurfaceRequests.get(requestId);
        if (entry) {
          entry.cleanup();
          this.pendingSurfaceRequests.delete(requestId);
        }
        cb();
      };

      const unsubComplete = ws.onSurfaceComplete((data) => {
        if (data.requestId !== requestId) return;
        finish(() => resolve(data.result));
      });

      const unsubError = ws.onError((code, message, errRequestId) => {
        // Per-request errors carry the originating requestId; connection-
        // level errors (no requestId) fail every pending entry. Ignore
        // errors tagged for a different request.
        if (errRequestId !== undefined && errRequestId !== requestId) return;
        finish(() => reject(new Error(`${code}: ${message}`)));
      });

      // Escalating poll fallback (same contract as the image path). The WS
      // `surface_complete` (live or replayed on subscribe) normally settles
      // first and cancels this.
      const cancelPoll = this.startPollFallback(
        () =>
          this.pollSurfaceStatus(requestId).then((s) =>
            s.kind === 'completed' ? { kind: 'completed' as const, value: s.result } : s,
          ),
        (outcome) => {
          if (outcome.type === 'completed') {
            finish(() => resolve(outcome.value));
          } else if (outcome.type === 'failed') {
            finish(() => reject(outcome.error));
          } else {
            finish(() => reject(new Error('surface_invoke_timeout')));
          }
        },
      );

      const cleanup = (): void => {
        unsubComplete();
        unsubError();
        cancelPoll();
        ws.unsubscribe(requestId);
      };

      this.pendingSurfaceRequests.set(requestId, { resolve, reject, cleanup });

      // Best-effort WS subscribe, detached so it can't block the poll. See
      // subscribeForAsyncResult (shared with the image path).
      void this.subscribeForAsyncResult(ws, requestId);
    });
  }

  /**
   * Polls `GET /v1/plugins/surface-status/:requestId` once. Mirrors
   * `pollImageStatus`; called only from the 180s safety-timer path.
   */
  private async pollSurfaceStatus(
    requestId: string,
  ): Promise<
    | { kind: 'pending' }
    | { kind: 'completed'; result: unknown }
    | { kind: 'failed'; code: string; message: string }
    | { kind: 'not_found' }
  > {
    try {
      // Session-cookie auth (see pollImageStatus): `/plugins/surface-status`
      // is `requireAuth`, so the host's browser session — not the bridge
      // capability token from `getAuthToken()` — is what authenticates here.
      const resp = await fetch(
        `${this.options.bridgeApiUrl}/plugins/surface-status/${encodeURIComponent(requestId)}`,
        { credentials: sessionCredentials() },
      );
      if (resp.status === 404) return { kind: 'not_found' };
      const body = (await resp.json()) as {
        status?: string;
        result?: unknown;
        error?: { code?: string; message?: string };
      };
      if (body.status === 'completed') return { kind: 'completed', result: body.result };
      if (body.status === 'failed') {
        return {
          kind: 'failed',
          code: body.error?.code ?? 'SURFACE_INVOCATION_FAILED',
          message: body.error?.message ?? 'Surface invocation failed',
        };
      }
      return { kind: 'pending' };
    } catch {
      return { kind: 'pending' };
    }
  }

  private proxyToBackend(msg: BridgeMessage): Promise<unknown> {
    return this.callBackendOp(msg.type, msg.payload);
  }

  /**
   * Stamp the HOST's acting workspace onto an outbound `entity_invoke`. Always
   * assigned — `undefined` when there is none, which `JSON.stringify` drops —
   * so a value the iframe put in the payload never survives to the server.
   */
  private withActingHolder(msg: BridgeMessage): BridgeMessage {
    const actingHolderId = this.options.getActingHolderId?.() ?? undefined;
    return {
      ...msg,
      payload: { ...(msg.payload as Record<string, unknown>), actingHolderId },
    };
  }

  /**
   * Bridge endpoint for an op. Image-bearing `entity_invoke` goes to the
   * dedicated `/bridge-vision` route (a higher, isolated body limit for inline
   * image bytes); everything else uses the 256 KB `/bridge` route.
   */
  private bridgeEndpoint(type: string, payload: unknown): string {
    const images = (payload as { images?: unknown })?.images;
    const hasImages = type === 'entity_invoke' && Array.isArray(images) && images.length > 0;
    const path = hasImages ? 'bridge-vision' : 'bridge';
    return `${this.options.bridgeApiUrl}/plugins/${path}/${this.options.archId}`;
  }

  /** POST a single bridge op to the backend from the (unrestricted) host. */
  private async callBackendOp(type: string, payload: unknown, isRetry = false): Promise<unknown> {
    const token = await this.options.getAuthToken();
    const response = await fetch(this.bridgeEndpoint(type, payload), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ type, payload }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Bridge call failed' }));
      // One-shot recovery for a token that expired between retrieval and
      // server verification — rare (the token caches refresh 30s early), but
      // reachable in a long-lived preview under clock drift or a paused tab.
      // The hook busts the caller's token cache so the retried getAuthToken()
      // mints fresh instead of reading the same stale token back. Strictly
      // BRIDGE_TOKEN_EXPIRED: other 401/403s (invalid token, permission
      // denied, arche mismatch) are never retried.
      if (
        !isRetry &&
        (err as { error?: { code?: string } }).error?.code === 'BRIDGE_TOKEN_EXPIRED'
      ) {
        this.options.onAuthTokenExpired?.();
        return this.callBackendOp(type, payload, true);
      }
      // HostBridgeError preserves the server's error code both for host-side
      // callers that classify failures (the subscription manager's
      // terminal-vs-transient renewal logic) and for the iframe-bound error
      // response (handleMessage forwards `code` as `errorCode`).
      throw new HostBridgeError(
        err.error?.message ?? 'Bridge call failed',
        (err as { error?: { code?: string } }).error?.code ?? 'BRIDGE_ERROR',
        errorRequestIdOf(err),
      );
    }

    return response.json();
  }

  /**
   * Host-orchestrated binary upload (see the `vault_documents_upload` case).
   * Runs init → presigned PUT → finalize in the host context so the iframe's
   * `connect-src 'none'` CSP never blocks it. The presigned `uploadUrl` is
   * returned by the backend (init), so the iframe can't redirect the PUT
   * (no SSRF). Returns the finalize result (`{ documentId, status }`).
   */
  private async handleVaultUpload(payload: unknown): Promise<unknown> {
    const p = (payload ?? {}) as {
      name?: unknown;
      mimeType?: unknown;
      bytes?: unknown; // base64
      tags?: unknown;
      sensitivity?: unknown;
      folderPath?: unknown;
      replacesDocumentId?: unknown;
      userVisible?: unknown;
      destination?: unknown;
      saveToDocumentId?: unknown;
    };
    // In-place save of an EXISTING document (the byte lane of the SDK's
    // `saveContent`). Branches on key PRESENCE, before the create-path
    // validation below, so a malformed save can never fall through and
    // create a new document instead.
    if (p.saveToDocumentId !== undefined) {
      return this.handleVaultInPlaceSave(payload);
    }
    if (
      typeof p.name !== 'string' ||
      typeof p.mimeType !== 'string' ||
      typeof p.bytes !== 'string'
    ) {
      throw new Error('vault_documents_upload: name, mimeType, and bytes (base64) are required');
    }
    const destination = uploadDestination(p);
    const u8 = base64ToBytes(p.bytes);

    // ── Into the user's Documents: the host Save dialog, never this op ──
    //
    // The user chooses the folder and the name, settles any clash, and decides
    // what the arche keeps; the HOST performs the write from the user's own
    // session. Nothing is created before they confirm, and a decline writes
    // nothing. The server independently requires the arche to be an active
    // plugin holding `vault:user-documents:write`.
    if (destination === 'documents') {
      for (const field of ['folderPath', 'replacesDocumentId', 'tags', 'sensitivity'] as const) {
        if (p[field] !== undefined) {
          throw new HostBridgeError(
            `vault_documents_upload: "${field}" does not apply to a save into the user's ` +
              'Documents — the user chooses where it goes and how it is kept.',
            'INVALID_PARAMS',
          );
        }
      }
      return this.saveIntoUserDocuments({ fileName: p.name, mimeType: p.mimeType, bytes: u8 });
    }

    // An automatic save into the user's Arche Saves: both gates are the
    // server's (the permission, and an admin designation). Checked here too
    // so an arche without the permission is told so plainly.
    if (destination === 'arche-saves' && !this.permissions.includes('vault:arche-saves:write')) {
      throw new HostBridgeError(
        "Saving into the user's Arche Saves requires the vault:arche-saves:write permission.",
        'PERMISSION_DENIED',
      );
    }

    const digest = await crypto.subtle.digest('SHA-256', u8);
    const checksumSha256 = bytesToHex(new Uint8Array(digest));
    const init = (await this.callBackendOp('vault_documents_upload_init', {
      name: p.name,
      mimeType: p.mimeType,
      sizeBytes: u8.byteLength,
      checksumSha256,
      tags: p.tags,
      sensitivity: p.sensitivity,
      folderPath: p.folderPath,
      replacesDocumentId: p.replacesDocumentId,
      ...(destination === 'arche-saves' ? { destination: 'arche-saves' } : {}),
    })) as { documentId: string; uploadUrl: string; requiredHeaders: Record<string, string> };

    // Browsers forbid setting Content-Length explicitly; strip it.
    const headers = Object.fromEntries(
      Object.entries(init.requiredHeaders ?? {}).filter(
        ([k]) => k.toLowerCase() !== 'content-length',
      ),
    );
    const putResp = await fetch(init.uploadUrl, { method: 'PUT', body: u8, headers });
    if (!putResp.ok) {
      const text = await putResp.text().catch(() => '');
      throw new Error(`Vault upload PUT failed (${putResp.status}): ${text || putResp.statusText}`);
    }

    return (await this.callBackendOp('vault_documents_upload_finalize', {
      documentId: init.documentId,
      checksumSha256,
    })) as Record<string, unknown>;
  }

  /**
   * Show the host Save dialog for bytes the arche handed over, and report what
   * the user saved. The arche learns the document id, the final name and the
   * access it keeps — never the folder.
   */
  private async saveIntoUserDocuments(request: {
    fileName: string;
    mimeType: string;
    bytes: Uint8Array<ArrayBuffer>;
  }): Promise<unknown> {
    this.assertMaySaveIntoUserFiles();
    if (!this.options.onVaultSaveRequest) {
      throw new HostBridgeError('This host cannot save into your Fias files.', 'SAVE_DECLINED');
    }
    const decision = await this.options.onVaultSaveRequest(request);
    if (!('saved' in decision)) {
      throw new HostBridgeError('You chose not to save this file.', 'SAVE_DECLINED');
    }
    // A new file is in the user's Documents; a Fias Files view cached moments
    // ago must not go on omitting it.
    invalidateVaultDocumentsAfterHostWrite();
    const { documentId, name, access } = decision.saved;
    return {
      documentId,
      status: 'uploaded',
      name,
      access,
      // Published alias (SDK ≤ 2.x): "keep editing" was granted.
      editAccess: access === 'write',
    };
  }

  /**
   * `saveCopy(documentId)` — a copy of one of the arche's OWN files into the
   * user's Documents, through the Save dialog. The host reads the bytes (the
   * iframe's CSP forbids the fetch), so the arche never touches the user's
   * tree; its working file is unchanged.
   */
  private async handleSaveCopy(payload: object): Promise<unknown> {
    const p = payload as { documentId?: unknown; saveCopy?: unknown };
    const unknownKey = Object.keys(p).find((k) => k !== 'documentId' && k !== 'saveCopy');
    if (unknownKey) {
      throw new HostBridgeError(
        `saveCopy: "${unknownKey}" cannot be combined with a copy — it changes nothing on the original.`,
        'INVALID_PARAMS',
      );
    }
    if (typeof p.documentId !== 'string' || !/^vdoc_[a-f0-9]{32}$/.test(p.documentId)) {
      throw new HostBridgeError('saveCopy: documentId must be a vdoc_* id', 'INVALID_PARAMS');
    }
    const options = (p.saveCopy ?? {}) as { suggestedName?: unknown };
    if (typeof options !== 'object' || options === null) {
      throw new HostBridgeError('saveCopy: options must be an object', 'INVALID_PARAMS');
    }
    if (
      options.suggestedName !== undefined &&
      (typeof options.suggestedName !== 'string' || options.suggestedName.length > 255)
    ) {
      throw new HostBridgeError(
        'saveCopy: suggestedName must be a string of 255 characters or fewer',
        'INVALID_PARAMS',
      );
    }
    if (!this.permissions.includes('vault:documents:read')) {
      throw new HostBridgeError(
        'saveCopy reads your own document first, which requires vault:documents:read.',
        'PERMISSION_DENIED',
      );
    }
    this.assertMaySaveIntoUserFiles();
    const source = (await this.callBackendOp('vault_documents_download_url', {
      documentId: p.documentId,
    })) as { url: string; contentType: string | null; name?: string | null };
    const response = await fetch(source.url);
    if (!response.ok) {
      throw new HostBridgeError(
        `saveCopy: the document could not be read (HTTP ${response.status}).`,
        'VAULT_DOCUMENTS_ERROR',
      );
    }
    const bytes = new Uint8Array(await response.arrayBuffer());
    return this.saveIntoUserDocuments({
      fileName: (options.suggestedName as string | undefined) ?? source.name ?? 'Untitled',
      mimeType: source.contentType ?? 'application/octet-stream',
      bytes,
    });
  }

  /**
   * Putting a file into the USER'S files needs `vault:user-documents:write`.
   * The server enforces it; checking here first means the user is never asked
   * to approve something that is then refused — the same reason the pick path
   * checks the edit permission before opening its sheet.
   */
  private assertMaySaveIntoUserFiles(): void {
    if (!this.permissions.includes('vault:user-documents:write')) {
      throw new HostBridgeError(
        "Saving into the user's files requires the vault:user-documents:write permission.",
        'PERMISSION_DENIED',
      );
    }
  }

  /**
   * Host-orchestrated IN-PLACE save — `saveContent()` for content the bridge
   * body cannot carry. Same shape as the upload above (init → presigned PUT
   * → finalize, all in the host because the iframe's CSP forbids the PUT),
   * pointed at an existing document: the two steps are the
   * `init_own_document_save` / `finalize_own_document_save` verbs on the
   * Vault Arche Documents entity, reached through the generic
   * `entity_invoke` op. No new bridge op (ADR-012: the composite host op
   * gained a field).
   *
   * The PUT targets a STAGING key the backend chose; the document itself is
   * only replaced by finalize, server-side, under its row lock — so a failed
   * or abandoned save here never damages the document.
   */
  private async handleVaultInPlaceSave(payload: unknown): Promise<unknown> {
    const p = (payload ?? {}) as Record<string, unknown>;
    const allowed = new Set([
      'saveToDocumentId',
      'saveTarget',
      'bytes',
      'mimeType',
      'contentKind',
      'expectedRevision',
    ]);
    const unknownKey = Object.keys(p).find((k) => !allowed.has(k));
    if (unknownKey) {
      // Refused, not ignored: `name`, `folderPath`, `userVisible`… belong to
      // CREATING a document, and a caller that sent one misunderstood which
      // of the two operations it asked for.
      throw new HostBridgeError(
        `vault_documents_upload: "${unknownKey}" cannot be combined with saveToDocumentId — an in-place save changes content only.`,
        'INVALID_PARAMS',
      );
    }
    if (typeof p.saveToDocumentId !== 'string' || typeof p.bytes !== 'string') {
      throw new HostBridgeError(
        'vault_documents_upload: saveToDocumentId and bytes (base64) are required for an in-place save',
        'INVALID_PARAMS',
      );
    }
    if (p.saveTarget !== undefined && p.saveTarget !== 'user' && p.saveTarget !== 'own') {
      throw new HostBridgeError(
        'vault_documents_upload: saveTarget must be "own" or "user"',
        'INVALID_PARAMS',
      );
    }
    const lane = inPlaceSaveLane(p.saveTarget);
    if (lane.isUserLane) {
      // It is the USER'S file. The server enforces both of these; refusing
      // here just means nothing is hashed or uploaded for a save that cannot
      // succeed.
      if (typeof p.expectedRevision !== 'string') {
        throw new HostBridgeError(
          "Saving to a document in the user's files requires expectedRevision.",
          'EXPECTED_REVISION_REQUIRED',
        );
      }
      if (p.mimeType !== undefined) {
        throw new HostBridgeError(
          "A document in the user's files keeps its type; mimeType cannot be set.",
          'MIME_CHANGE_NOT_ALLOWED',
        );
      }
    }
    if (p.contentKind !== undefined && p.contentKind !== 'text' && p.contentKind !== 'binary') {
      // Refused, not dropped: `contentKind: 'text'` is what makes the server
      // hold a string save to a text type, so a typo silently skipping that
      // check is the wrong failure. (The dev harness refuses it too.)
      throw new HostBridgeError(
        'vault_documents_upload: contentKind must be "text" or "binary"',
        'INVALID_PARAMS',
      );
    }
    // Size BEFORE decoding or hashing: base64 is 4 chars per 3 bytes, so the
    // length bounds the payload without allocating it. The server is the real
    // gate; this just spares the main thread a 15 MB+ decode for a save that
    // cannot succeed.
    if (Math.floor((p.bytes.length * 3) / 4) - 2 > MAX_IN_PLACE_SAVE_BYTES) {
      throw new HostBridgeError(
        `Content is too large; an in-place save accepts at most ${MAX_IN_PLACE_SAVE_BYTES} bytes (15 MB).`,
        'OWN_DOCUMENT_SAVE_TOO_LARGE',
      );
    }
    let u8: Uint8Array<ArrayBuffer>;
    try {
      u8 = base64ToBytes(p.bytes);
    } catch {
      // `atob` throws a DOMException on malformed input — a caller error, and
      // it should read as one.
      throw new HostBridgeError(
        'vault_documents_upload: bytes is not valid base64',
        'INVALID_PARAMS',
      );
    }
    const digest = await crypto.subtle.digest('SHA-256', u8);
    const checksumSha256 = bytesToHex(new Uint8Array(digest));
    const invoke = async <T>(op: string, args: Record<string, unknown>): Promise<T> => {
      const res = (await this.callBackendOp('entity_invoke', {
        entityId: lane.entityId,
        payload: { op, ...args },
      })) as { output: T };
      return res.output;
    };

    const init = await invoke<{
      saveId: string;
      uploadUrl: string;
      requiredHeaders: Record<string, string>;
    }>(lane.initOp, {
      documentId: p.saveToDocumentId,
      sizeBytes: u8.byteLength,
      checksumSha256,
      ...(typeof p.mimeType === 'string' ? { contentType: p.mimeType } : {}),
      ...(p.contentKind !== undefined ? { contentKind: p.contentKind } : {}),
      ...(typeof p.expectedRevision === 'string' ? { expectedRevision: p.expectedRevision } : {}),
    });

    // Browsers forbid setting Content-Length explicitly; strip it.
    const headers = Object.fromEntries(
      Object.entries(init.requiredHeaders ?? {}).filter(
        ([k]) => k.toLowerCase() !== 'content-length',
      ),
    );
    const putResp = await fetch(init.uploadUrl, { method: 'PUT', body: u8, headers });
    if (!putResp.ok) {
      const text = await putResp.text().catch(() => '');
      throw new Error(`Vault save PUT failed (${putResp.status}): ${text || putResp.statusText}`);
    }

    const finalizeArgs = {
      documentId: p.saveToDocumentId,
      saveId: init.saveId,
      checksumSha256,
      ...(typeof p.expectedRevision === 'string' ? { expectedRevision: p.expectedRevision } : {}),
    };
    // The bytes are already uploaded; losing the save to a throttle on the
    // LAST step would waste them. Retried only for the two refusals the
    // server gives WITHOUT consuming the staged object — RATE_LIMIT (throttled,
    // or at its save-concurrency cap) and DOCUMENT_BUSY (the document's lock
    // was held) — so a retry with the same saveId finds its bytes intact.
    // Every other refusal is final: the server has discarded them.
    const finalize = async (): Promise<unknown> => {
      for (const delayMs of FINALIZE_RATE_LIMIT_RETRY_DELAYS_MS) {
        try {
          return await invoke(lane.finalizeOp, finalizeArgs);
        } catch (err) {
          if (!(err instanceof HostBridgeError) || !FINALIZE_RETRYABLE_CODES.has(err.code)) {
            throw err;
          }
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      }
      return invoke(lane.finalizeOp, finalizeArgs);
    };
    const saved = await finalize();
    // A file in the USER'S files just changed size, date and "last changed
    // by". Fias Files shows all three; an arche-private document appears in no
    // host view, so the own lane has nothing to refresh.
    if (lane.isUserLane) invalidateVaultDocumentsAfterHostWrite();
    return saved;
  }

  /**
   * Shared store (IAP) handling for BOTH the legacy `store_*` alias ops and the
   * generic `entity_invoke` path (ADR-012 Phase L, Job 3). Reproduces the store
   * family's host-mediated behavior independent of transport: preview
   * simulation, the purchase-confirmation overlay (which gates a charge
   * client-side before any backend call), and the legacy wire SHAPES (bare
   * arrays / `{ success, ... }`). `proxy` performs the real backend call for a
   * store verb and MUST return the executor result already unwrapped to the
   * legacy shape — the alias transport returns it bare; the `entity_invoke`
   * transport unwraps the `{ output }` envelope first — so this method never
   * sees the envelope. Backend rejections propagate (throw) exactly as before.
   */
  private async handleStoreOp(
    op: string,
    args: Record<string, unknown>,
    proxy: (op: string, args: Record<string, unknown>) => Promise<unknown>,
  ): Promise<unknown> {
    // The Forge test channel shares ONLY this simulated store branch with the
    // preview (see `testChannel` on the options).
    if (this.options.previewMode || this.options.testChannel) {
      // if/else (not a switch) on purpose: `case '<verb>':` labels here would be
      // picked up by the bridge-op-freeze / permission-coverage source scanners
      // as if they were new bridge ops.
      if (op === 'get_products') {
        return this.options.previewStoreProducts ?? [];
      }
      if (op === 'purchase') {
        // Only products the preview store KNOWS (the manifest's declared
        // products, passed as previewStoreProducts) can be "purchased" —
        // and the result is explicitly simulated. The old stub succeeded
        // unconditionally: a purchase of a nonexistent product "worked"
        // with no confirmation sheet, the same fake-success preview lie as
        // the old vault stubs (kitchen-sink finding, 2026-07-28).
        const { productIdentifier } = args as { productIdentifier?: string };
        const product = (this.options.previewStoreProducts ?? []).find(
          (p) => p.productIdentifier === productIdentifier,
        );
        if (!product) {
          return {
            success: false,
            code: 'PRODUCT_NOT_FOUND',
            error: `Product "${String(productIdentifier)}" is not declared in fias-plugin.json products — the preview store only knows declared products.`,
          };
        }
        return {
          success: true,
          // Marks the purchase as a preview simulation: no credits moved, no
          // entitlement persisted. Additive field; published results never
          // carry it.
          simulated: true,
          purchaseId: `preview_apc_${Date.now()}`,
          product: {
            productIdentifier: product.productIdentifier,
            productType: product.type,
            name: product.name,
          },
        };
      }
      if (op === 'get_entitlements' || op === 'restore') {
        return [];
      }
      if (op === 'get_purchase_history') {
        return { purchases: [] };
      }
      return { success: true };
    }
    // Published mode: the purchase-confirmation overlay gates `purchase`
    // client-side BEFORE any charge (unchanged from the legacy alias path).
    if (op === 'purchase' && this.options.onPurchaseRequest) {
      const { productIdentifier } = args as { productIdentifier?: string };
      const products = (await proxy('get_products', {})) as Array<{
        productIdentifier: string;
        name: string;
        description: string | null;
        priceCredits: number;
        type: string;
      }>;
      const product = Array.isArray(products)
        ? products.find((p) => p.productIdentifier === productIdentifier)
        : undefined;
      if (!product) {
        return { success: false, error: 'Product not found' };
      }
      return this.options.onPurchaseRequest(product);
    }
    return proxy(op, args);
  }

  /**
   * Resolve the rate-limit bucket key + per-minute cap for a call. For
   * `entity_invoke` on a migrated op-family (ADR-012 Phase L), this is
   * per-(entityId, verb) so storage/datastore/workspaces/store keep their
   * distinct caps instead of collapsing into the single `entity_invoke` bucket.
   * Everything else (including AI/text `entity_invoke`, datastore search /
   * subscribe) keys by op type. Returns null when the op has no configured cap.
   */
  private resolveRateLimit(
    type: string,
    payload: unknown,
  ): {
    key: string;
    maxPerMinute: number;
    units: number;
    /** Shared per-entity bucket the call must ALSO fit (see
     *  ENTITY_INVOKE_ENTITY_TOTALS). */
    total?: { key: string; maxPerMinute: number; units: number };
  } | null {
    // The in-place save branch of the composite upload op is, to the server,
    // an init + a finalize on the Vault Arche Documents entity — and the host
    // drives both through `callBackendOp`, which bypasses this limiter. So it
    // is charged HERE, as what it really is: one unit of the init bucket and
    // two of the entity total.
    if (
      type === 'vault_documents_upload' &&
      typeof (payload as { saveToDocumentId?: unknown } | null)?.saveToDocumentId === 'string'
    ) {
      const lane = inPlaceSaveLane((payload as { saveTarget?: unknown }).saveTarget);
      const entityId = lane.entityId;
      return {
        key: entityVerbRateKey(entityId, lane.initOp),
        maxPerMinute: ENTITY_INVOKE_RATE_LIMITS[entityId][lane.initOp],
        units: 1,
        total: {
          key: `entity_invoke_total:${entityId}`,
          maxPerMinute: ENTITY_INVOKE_ENTITY_TOTALS[entityId],
          units: 2,
        },
      };
    }
    // A promote rides `vault_documents_update`, but what it does is open a
    // host MODAL. On the update op's 60/min a plugin could put that modal back
    // in front of the user about once a second after every "Not now". It is
    // charged to the picker's bucket — the SAME bucket, not a second one of the
    // same size: the budget is for how often a plugin may summon host consent
    // UI, and two buckets would simply double it.
    if (
      type === 'vault_documents_update' &&
      payload !== null &&
      typeof payload === 'object' &&
      'userVisible' in payload
    ) {
      return {
        key: 'vault_documents_pick',
        maxPerMinute: RATE_LIMITS.vault_documents_pick.maxPerMinute,
        units: 1,
      };
    }
    if (type === 'entity_invoke') {
      const p = payload as {
        entityId?: unknown;
        payload?: { op?: unknown; operations?: unknown };
      } | null;
      const entityId = typeof p?.entityId === 'string' ? p.entityId : '';
      const op = typeof p?.payload?.op === 'string' ? p.payload.op : '';
      const perVerb = ENTITY_INVOKE_RATE_LIMITS[entityId]?.[op];
      if (perVerb !== undefined) {
        // ADR-016 Phase A: a weighted verb (see ENTITY_INVOKE_RATE_WEIGHTS)
        // consumes one unit per started opsPerUnit operations, so the 100-op
        // batch cap raise did not raise client-granted write throughput.
        const opsPerUnit = ENTITY_INVOKE_RATE_WEIGHTS[entityId]?.[op];
        const units =
          opsPerUnit !== undefined && Array.isArray(p?.payload?.operations)
            ? Math.max(1, Math.ceil(p.payload.operations.length / opsPerUnit))
            : 1;
        const entityTotal = ENTITY_INVOKE_ENTITY_TOTALS[entityId];
        return {
          key: entityVerbRateKey(entityId, op),
          maxPerMinute: perVerb,
          units,
          ...(entityTotal !== undefined
            ? {
                total: {
                  key: `entity_invoke_total:${entityId}`,
                  maxPerMinute: entityTotal,
                  units: 1,
                },
              }
            : {}),
        };
      }
    }
    // Client-side entities each get their own host bucket (ADR-021, plan
    // §10.3). One shared `client_entity_invoke` key let a busy entity — a
    // course loading its lessons — starve every other client entity in the
    // same plugin, burst window included. The bucket is the entity's own
    // per-minute limit, which the registry also enforces per
    // (user, arche, entity). An unregistered id falls through to the shared
    // bucket; the handler refuses it anyway.
    if (type === 'client_entity_invoke') {
      const entityId = (payload as { entityId?: unknown } | null)?.entityId;
      const registration =
        typeof entityId === 'string' ? getClientEntityRegistration(entityId) : undefined;
      if (registration) {
        return {
          key: `client_entity_invoke:${entityId as string}`,
          maxPerMinute: registration.rateLimitPerMinute,
          units: 1,
        };
      }
    }
    const limit = RATE_LIMITS[type];
    return limit ? { key: type, maxPerMinute: limit.maxPerMinute, units: 1 } : null;
  }

  private checkRateLimit(type: string, payload: unknown): void {
    const resolved = this.resolveRateLimit(type, payload);
    // Burst detection first (catches runaway loops in <5s) — keyed the same way
    // so per-verb entity_invoke calls get independent burst windows too.
    this.checkBurst(resolved?.key ?? type, type, payload);

    if (!resolved) return;

    // Weighted (ADR-016 Phase A) and all-or-nothing, mirroring the server: a
    // call that does not fit charges nothing, so an over-sized batch cannot
    // starve the caller's own remaining budget. A call with a shared entity
    // total must fit BOTH buckets before either is charged.
    const now = Date.now();
    const charges = resolved.total ? [resolved, resolved.total] : [resolved];
    for (const charge of charges) {
      const bucket = this.rateLimits.get(charge.key);
      const used = bucket && now - bucket.windowStart <= 60_000 ? bucket.count : 0;
      if (used + charge.units > charge.maxPerMinute) {
        const target = describeBridgeTarget(type, payload);
        const targetSuffix = target ? ` (${target})` : '';
        // Coded, like the server's refusal, so a plugin's
        // `err.code === 'RATE_LIMIT'` branch fires whichever side said no.
        throw new HostBridgeError(
          `Rate limit exceeded for ${type}${targetSuffix}: max ${charge.maxPerMinute}/minute`,
          'RATE_LIMIT',
        );
      }
    }
    for (const charge of charges) {
      const bucket = this.rateLimits.get(charge.key);
      if (!bucket || now - bucket.windowStart > 60_000) {
        this.rateLimits.set(charge.key, { count: charge.units, windowStart: now });
      } else {
        bucket.count += charge.units;
      }
    }
  }

  private checkBurst(bucketKey: string, displayType: string, payload: unknown): void {
    const now = Date.now();
    const bucket = this.burstBuckets.get(bucketKey);
    const target = describeBridgeTarget(displayType, payload);
    const targetSuffix = target ? ` (${target})` : '';

    // If currently in cooldown, block silently (onLog already fired on first breach)
    if (bucket?.blockedUntil && now < bucket.blockedUntil) {
      throw new BridgeBurstError(
        `Bridge call "${displayType}"${targetSuffix} temporarily blocked — rapid-fire loop detected. ` +
          `Check useEffect dependency arrays, or debounce writes if this is a usePersistentState key being updated at high frequency.`,
      );
    }

    // Reset expired burst windows
    if (!bucket || now - bucket.windowStart > BURST_WINDOW_MS) {
      this.burstBuckets.set(bucketKey, { count: 1, windowStart: now });
      return;
    }

    bucket.count++;

    if (bucket.count > BURST_THRESHOLD) {
      bucket.blockedUntil = now + BURST_COOLDOWN_MS;
      const msg =
        `Runaway loop detected: "${displayType}"${targetSuffix} called ${bucket.count} times in ${BURST_WINDOW_MS / 1000}s. ` +
        `Calls blocked for ${BURST_COOLDOWN_MS / 1000}s. ` +
        `Check useEffect dependency arrays, or debounce writes if this is a usePersistentState key being updated at high frequency.`;
      this.options.onLog?.('error', msg);
      throw new BridgeBurstError(
        `Bridge call "${displayType}"${targetSuffix} temporarily blocked — rapid-fire loop detected.`,
      );
    }
  }

  private sendToPlugin(message: BridgeMessage): void {
    // targetOrigin '*' is required because sandboxed iframes without allow-same-origin
    // have an opaque (null) origin. Security is enforced by checking event.source
    // against iframe.contentWindow on incoming messages (line 113).
    this.options.iframe.contentWindow?.postMessage(message, '*');
  }

  private sendResponse(messageId: string, payload: unknown): void {
    this.sendToPlugin({
      type: 'response' as string,
      messageId,
      payload,
    });
  }

  private sendError(
    messageId: string,
    error: string,
    errorCode?: string,
    errorRequestId?: string,
  ): void {
    // See sendToPlugin() for why '*' is used as targetOrigin. `errorCode` and
    // `errorRequestId` are plain wire fields: the SDK constructs a
    // FiasBridgeError from them locally (never structured-clones an Error).
    this.options.iframe.contentWindow?.postMessage(
      {
        type: 'response',
        messageId,
        payload: null,
        error,
        ...(errorCode !== undefined ? { errorCode } : {}),
        ...(errorRequestId !== undefined ? { errorRequestId } : {}),
      },
      '*',
    );
  }
}

// ── Binary helpers for the host-orchestrated vault upload ────────────────────

/**
 * Where a `vault_documents_upload` goes: the arche's own space (undefined, the
 * default), the user's Documents through the host Save dialog
 * (`destination: 'documents'`, or its published alias `userVisible: true`), or
 * — for an arche an admin designated — the user's Arche Saves automatically
 * (`destination: 'arche-saves'`). Anything else is refused, never guessed.
 */
function uploadDestination(p: {
  destination?: unknown;
  userVisible?: unknown;
}): 'documents' | 'arche-saves' | undefined {
  if (p.userVisible !== undefined && typeof p.userVisible !== 'boolean') {
    throw new HostBridgeError(
      'vault_documents_upload: userVisible must be a boolean',
      'INVALID_PARAMS',
    );
  }
  if (p.destination === undefined) return p.userVisible === true ? 'documents' : undefined;
  if (p.destination !== 'documents' && p.destination !== 'arche-saves') {
    throw new HostBridgeError(
      `vault_documents_upload: unknown destination "${String(p.destination)}"`,
      'INVALID_PARAMS',
    );
  }
  if (p.destination === 'documents' && p.userVisible === false) {
    throw new HostBridgeError(
      "vault_documents_upload: destination 'documents' contradicts userVisible: false",
      'INVALID_PARAMS',
    );
  }
  if (p.destination === 'arche-saves' && p.userVisible === true) {
    throw new HostBridgeError(
      "vault_documents_upload: userVisible means the Save dialog; it contradicts destination 'arche-saves'",
      'INVALID_PARAMS',
    );
  }
  return p.destination;
}

function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i++) out += bytes[i].toString(16).padStart(2, '0');
  return out;
}
